/**
 * SpreadsTotalsTable — moneyline-style table for spread + total markets.
 * Same column structure as GameTable (DATE | TEAM | KALSHI | K VOL | K LIQ |
 * POLY | P VOL | P LIQ); per-event zebra; thick top border between events.
 *
 * Each event renders 5 rows: away team label, spread action row (chip strip +
 * Y/N pills + market data), home team label, total label row, total action
 * row. The team currently on the YES side of the spread ticker (favored at
 * the selected line) is highlighted; the underdog is muted.
 *
 * Spreads share one Kalshi ticker per line (YES = favored team covers); a
 * single Y/N pill pair shows that ticker's prices. Totals share one ticker
 * per strike (YES = Over, NO = Under); a single pair labeled O/U shows
 * both sides at once.
 *
 * Clicking the action row expands a ladder below it. The toolbar YES/NO
 * toggle decides which side's ladder renders — same convention as
 * GameTable's renderLadderRows.
 */

import { LineTheoreticalRoi } from './LineTheoreticalRoi';
import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import { LiquidityBar } from '@/components/atoms/LiquidityBar';
import { Th, Td } from '@/components/atoms/TableCells';
import {
  depthDeltaTone,
  depthKeyForBook,
  depthVelocityDisplay,
} from '@/components/cards/bookDepthVelocity';
import { DepthVelocityValue } from '@/components/cards/DepthVelocityValue';
import { formatCompactCurrency } from '@/lib/infra/formatters';
import { AggregateLiquidityCell } from '../ValueDashboard/GameTable';
import {
  KALSHI_TOTAL_VOLUME_LABEL,
  POLY_TOTAL_VOLUME_LABEL,
  VOL_HEADER_TOOLTIP_KALSHI,
  VOL_HEADER_TOOLTIP_POLY,
} from '../ValueDashboard/sharedBookCue';
import { parseTicker } from '@/lib/kalshiTicker/parse';
import { useMarketStore } from '@/stores/marketStore';
import type { KalshiOrder } from '@/types/kalshi';
import type {
  UserOrderSlice,
  UserPositionSlice,
} from '@/hooks/useUserKalshiPOS';
import type {
  GameEventRow,
  SpreadLine,
  TotalLine,
  MarketSide,
  PriceMode,
  BookLevel,
  VenueBook,
  Sport,
} from '../ValueDashboard/types';
import { americanOdds } from '../ValueDashboard/tradeModalBook';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useLt2Access } from '@/hooks/useLt2Access';
import { useFeeDisplayStatus, useFeeFor } from '@/services/hydraFees';
import {
  FEE_DISPLAY_MESSAGE,
  FeeScheduleMarker,
  feeDisplayCanHide,
} from '@/components/molecules/FeeScheduleMarker';
import {
  LADDER_LEVELS,
  buildSideLevels,
  crossedBookRead,
  formatVolume,
  ladderRowPricePresentation,
  pillPriceCents,
  queueDepth,
  orderTooltip,
  type BidRowOrder,
} from '../ValueDashboard/ladder/helpers';
import { PricePill } from '../ValueDashboard/ladder/PricePill';
import { TwoSidedLadder, type DerivativeLadderPresentation } from './TwoSidedLadder';
import { NearestLinesPanel } from './NearestLinesPanel';
import { PosRing } from '../ValueDashboard/ladder/PosRing';
import { heldContractsByLineTitle } from '../ValueDashboard/ladder/heldContractsTitle';
import { RestingOrderGroup } from '../ValueDashboard/ladder/RestingOrderGroup';
import { GameCountdown, LiveBadge, NearEventStartPulse, UnknownStatusBadge } from '../ValueDashboard/GameTimeIndicators';
import { useGameStateFor } from '@/stores/gameStatesStore';
import { resolveLiveDisplayState } from '@/lib/sport/liveDisplayState';
import { formatGameStatus } from '@/lib/sport/periodLabel';
import { gameDetailHrefForRow, toHydraGameKeyLive } from '@/services/analyticsGameKey';
import { MiniLineStrip } from './MiniLineStrip';
import { StripWorkingOrderLinks } from './StripWorkingOrderLinks';
import {
  buildStripActivityLines,
  workingOrderActivityLines,
  workingOrderTickerSet,
  type StripActivityFact,
  type StripActivityLine,
} from './stripActivity';
import {
  fairPctText,
  opLineCanPush,
  OP_INTEGER_LINE_NOTE,
  type OpFairView,
} from '@/lib/opOdds';
import { formatSignedLine, linePriceCents, nearestLineWindow, spreadLineKey, totalLineKey } from './synthesize';
import { resolveLineReferenceView } from './butterflyContext';
import {
  classifyLineAvailability,
  LINE_AVAILABILITY_LABEL,
} from '@/lib/sport/lineAvailability';
import { deriveAttackTarget } from '@/lib/trading/attackTarget';
import { synthesizeFromSpread, synthesizeFromTotal } from './synthesize';
import { useLt2FocusStore, type Lt2CardTargets, type Lt2HotkeyTarget } from '@/stores/lt2FocusStore';
import { useLt2Card } from '@/hooks/useLt2Card';
import { isLt2FocusRegionClick } from '@/lib/trading/lt2FocusRegion';
import { Lt2FocusBadge } from '@/components/Lt2FocusBadge';
import type { Lt2Period, Lt2Surface } from '@/lib/trading/liveIntent';
import { useMakerRebateActive } from '@/hooks/useMakerRebate';
import { makerRebatePct } from '@/lib/pricing/makerRebate';
import { mirrorCents } from '@/lib/pricing/subCent';

/** Look up the live hydra market frame for an order ticker and parse the
 *  canonical line out of its `subtitle` (e.g. "Over 2.5 runs scored",
 *  "Texas wins by over 1.5 runs"). Returns null when no frame is in the
 *  store yet — caller falls back to per-sport strike math. */
function parseLineFromOrderTicker(
  ticker: string,
  _kind: 'spread' | 'total',
): number | null {
  const mkt = useMarketStore.getState().getMarket(ticker);
  const subtitle = mkt?.subtitle;
  if (!subtitle) return null;
  const m = subtitle.match(/\b(?:over|under) (\d+(?:\.\d+)?)\b/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/** Tiny chip-friendly format. Spreads are signed: `+2.5`/`−5.5`. Totals
 *  are positive line magnitudes: `2.5`. Trailing `.0` stripped so whole
 *  half-points render as `+2.5` not `+2.50`. */
function formatOrderLine(line: number, kind: 'spread' | 'total'): string {
  if (kind === 'total') return String(line);
  if (line > 0) return `+${line}`;
  if (line < 0) return `−${Math.abs(line)}`;
  return '0';
}

/** One held line inside a strip — the position slice plus the line it sits on
 *  (null when the line could not be resolved). Drives the per-line hover on
 *  the 'C' ring, whose counts otherwise span every line in the strip. */
interface StripPosLine {
  line: number | null;
  pos: UserPositionSlice;
}

/** Merge accountStore.openOrders (canonical user-order list, no queue depth)
 *  with snapshot ordersByTicker (queue_position when available) into a
 *  unified `UserOrderSlice[]` the ladder can render. accountStore is the
 *  source-of-truth for "what orders exist"; the snapshot just decorates
 *  each with `queue_position` when the relay returned it. Without this
 *  merge, queue chips silently disappear whenever the snapshot drops a
 *  ticker (large ticker filters, snapshot poll race, etc.). */
/** Exported for the #666 provenance pin (queueProvenance.test.ts); not part
 *  of the public component surface. */
export function mergeOrdersForTicker(
  ticker: string,
  openOrders: KalshiOrder[],
  ordersByTicker: Map<string, UserOrderSlice[]>,
): UserOrderSlice[] {
  const slicesFromSnapshot = ordersByTicker.get(ticker) ?? [];
  // #666: carry the FULL queue-display evidence, not just the number — the
  // retained flag and last-confirmed timestamp are what let orderTooltip
  // present a held value as "last confirmed Ns ago" instead of passing it
  // off as a live reading.
  const queueByOrderId = new Map<
    string,
    Pick<UserOrderSlice, 'queuePosition' | 'queueRetained' | 'queueConfirmedAtMs' | 'queueConfirmedAtMonoMs'>
  >();
  for (const s of slicesFromSnapshot) {
    queueByOrderId.set(s.orderId, {
      queuePosition: s.queuePosition,
      queueRetained: s.queueRetained,
      queueConfirmedAtMs: s.queueConfirmedAtMs,
      queueConfirmedAtMonoMs: s.queueConfirmedAtMonoMs,
    });
  }

  const out: UserOrderSlice[] = [];
  for (const o of openOrders) {
    if (o.ticker !== ticker) continue;
    if (o.remainingCount <= 0) continue;
    // The user-placed price for a NO order comes back as `noPrice` (cents on
    // the NO side). But Kalshi frequently sends only the YES leg even for a NO
    // order (CLAUDE.md venue quirk), so parseOrders leaves noPrice undefined and
    // the side-specific field alone silently DROPS the order from the overlay.
    // Fall back to the 100− complement of the opposite leg — every sibling
    // order-price site already does (PositionSidebar / orderCardModel / PositionTracker).
    const sidePrice = o.side === 'yes' ? o.yesPrice : o.noPrice;
    const oppPrice = o.side === 'yes' ? o.noPrice : o.yesPrice;
    // Exact mirror on the centi-cent grid: a float `100 - p` lands off it
    // (100 - 87.3 is 12.700000000000003) and this value is the exact-match key
    // the ladder buckets own orders by, so a drifted complement hides a resting
    // sub-cent order from its own rung.
    const priceCents =
      sidePrice != null ? sidePrice : oppPrice != null ? mirrorCents(oppPrice) : null;
    if (priceCents == null) continue;
    const queue = queueByOrderId.get(o.orderId);
    out.push({
      orderId: o.orderId,
      side: o.side,
      action: o.action,
      priceCents,
      remainingCount: o.remainingCount,
      // Snapshot's queue evidence when present; null otherwise (chip will
      // render as `count/?` so the user still sees their working size).
      queuePosition: queue?.queuePosition ?? null,
      queueRetained: queue?.queueRetained,
      queueConfirmedAtMs: queue?.queueConfirmedAtMs,
      queueConfirmedAtMonoMs: queue?.queueConfirmedAtMonoMs,
    });
  }
  return out;
}

// ── Per-row identification ───────────────────────────────────────────

/** One expandable action row per market type per event. */
export type RowKind = 'spread' | 'total';

/** The single expanded row across ALL events, or null when none is open.
 *  Exactly one detail panel renders, so the state is one value — not a
 *  per-event map whose "at most one entry" invariant every writer has to
 *  remember (three of them forgot; see #215 / #219). */
export type ExpandedRow = { eventKey: string; rowKind: RowKind } | null;

/** D-020 — click provenance for a pill/ladder trade. Optional and additive:
 *  the ticket handoff ignores it (flag-off payloads unchanged); the LT2
 *  one-click interception layer alone derives postOnly from it —
 *  `origin==='ladder' ? kind==='bid' : priceMode==='maker'` — restoring the
 *  bid-join vs ask-cross semantics the old wiring dropped. `lineIdx` names a
 *  non-selected alternate line (multi-line view); absent = the selected line. */
export interface PillTradeMeta {
  origin: 'pill' | 'ladder';
  kind?: 'bid' | 'ask';
  lineIdx?: number;
}

interface SpreadsTotalsTableProps {
  rows: GameEventRow[];
  /** The board's own name, when it is NARROWER than the rows' sport — a CFB
   *  subdivision bucket ("CFB · FBS"). Absent means the board is the whole
   *  sport, and the title falls back to the row's sport as before. */
  boardLabel?: string;
  marketSide: MarketSide;
  priceMode: PriceMode;
  /** Per-user Kalshi positions and open orders (keyed by ticker). Drives
   *  the inline 'O'-ring on each action row and the per-order qty/queue
   *  chips on bid rows of the inline ladder. Same source moneyline uses. */
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  /** Canonical list of user open orders from accountStore. Walked per
   *  event to aggregate C/O ring counts + line-value chips even on
   *  strikes hydra hasn't surfaced in `rows`. */
  openOrders: KalshiOrder[];
  /** Per-event line index for the spread strip (one shared between
   *  spread-away and spread-home rows). */
  spreadIdxByEvent: Map<string, number>;
  /** Per-event line index for the total strip. */
  totalIdxByEvent: Map<string, number>;
  /** The one row (if any) currently expanded inline as a ladder, across
   *  all events. Narrowed to `RowKind | null` per event on the way down. */
  expandedRow: ExpandedRow;
  onSelectSpreadIdx: (eventKey: string, idx: number) => void;
  onSelectTotalIdx: (eventKey: string, idx: number) => void;
  onToggleExpand: (eventKey: string, row: RowKind) => void;
  /** Fired when the user clicks an individual Y/N pill — feeds the
   *  parent's onTradeClick, which seeds the GameDetailPanel order ticket.
   *  Distinct from `onToggleExpand` so a row-body click can still toggle
   *  the inline ladder without seeding the ticket. */
  onPillTrade?: (
    eventKey: string,
    row: RowKind,
    venue: 'kalshi' | 'polymarket',
    side: 'yes' | 'no',
    cents: number,
    meta?: PillTradeMeta,
  ) => void;
}

/**
 * #608 — the ONE place a hotkey-focus card id is built. Owners that need to
 * clear a stale focus must derive the id through this helper: a second
 * hand-built template string is free to drift from the id the card actually
 * registered, and a clear that misses is indistinguishable from no clear.
 */
export function lt2CardId(
  keyPrefix: string,
  eventKey: string,
  kind: 'spread' | 'total',
): string {
  return `${keyPrefix}:${eventKey}:${kind}`;
}

/** Stable scope object — the dashboard board is always full-game. */
export const LT2_DASH_CARD_SCOPE = {
  surface: 'dash-spread-total',
  period: 'full',
  keyPrefix: 'st',
} as const;

export function SpreadsTotalsTable({
  rows,
  boardLabel,
  marketSide,
  priceMode,
  positionByTicker,
  ordersByTicker,
  openOrders,
  spreadIdxByEvent,
  totalIdxByEvent,
  expandedRow,
  onSelectSpreadIdx,
  onSelectTotalIdx,
  onToggleExpand,
  onPillTrade,
}: SpreadsTotalsTableProps) {
  // Keep the opted-in comparison column mounted through source withdrawal.
  // Auto-hiding it moved both the price columns and the expanded book during
  // live updates. The OP control still removes it deliberately.
  const showOpColumn = useDisplaySettings((s) => s.showOpOdds);

  // D-020 LT2-005 — the dashboard board's Poly-only reveal toggle. Hidden
  // without lt2Access, so the title bar is pixel-identical pre-flag.
  const lt2Access = useLt2Access();
  const showPolyOnlyLines = useDisplaySettings((s) => s.spreadTotalShowPolyOnlyLines);
  const setShowPolyOnlyLines = useDisplaySettings((s) => s.setSpreadTotalShowPolyOnlyLines);
  const butterflyOn = useDisplaySettings((s) => s.spreadTotalNearestLines) && lt2Access;
  return (
    <div className="bg-card border-border rounded-lg border">
      {/* Title bar — sport derived from the first row. */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">
            {boardLabel ?? rows[0]?.sport?.toUpperCase() ?? ''} Spread/Total
          </h2>
          <span className="text-muted-foreground text-xs">&mdash;</span>
          <span className="text-muted-foreground font-mono text-xs">
            {rows[0] ? `KX${rows[0].sport.toUpperCase()}SPREAD / KX${rows[0].sport.toUpperCase()}TOTAL` : ''}
          </span>
        </div>
        {lt2Access && !butterflyOn && (
          <button
            type="button"
            onClick={() => setShowPolyOnlyLines(!showPolyOnlyLines)}
            title={
              showPolyOnlyLines
                ? 'Showing Polymarket-only lines (order entry disabled on them). Click to hide.'
                : 'Polymarket-only lines are hidden from the execution view. Click to show them (disabled).'
            }
            className={cn(
              'rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors',
              showPolyOnlyLines
                ? 'border-primary/50 bg-primary/15 text-primary'
                : 'border-border/60 text-muted-foreground hover:text-foreground',
            )}
          >
            Poly-only
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <Th className="w-28">Date</Th>
              <Th className="w-72">Side &amp; Line</Th>
              {showOpColumn && (
                <Th
                  className="w-24 text-center"
                  title="OddsPapi cross-book de-vigged fair value AT the row's selected line (hydracore feed). Shown as YES/NO (or Over/Under) tenths; hover a cell for books, anchor, and line notes. Column hides when the OP source serves no line ladders — OG failover is moneyline-only, so lines emptying there is expected coverage behavior, not an error."
                >OP/PNCL</Th>
              )}
              <Th className="w-32 text-center">Kalshi</Th>
              <Th
                className="w-24 text-right"
                title={VOL_HEADER_TOOLTIP_KALSHI}
              >{KALSHI_TOTAL_VOLUME_LABEL}</Th>
              <Th className="w-24 text-right" title="Market row: total $ resting in book (bids + asks). Expanded ladder rows: Resting $ — price × size resting at that price.">K Liq</Th>
              <Th
                className="w-16 text-right"
                title="Kalshi best-bid depth change over the last 1m on the active side. Positive = building; negative = draining. Empty until 1m of samples accumulates."
              >K Δ/m</Th>
              {!butterflyOn && <>
              <Th className="w-32 text-center">Poly</Th>
              <Th
                className="w-24 text-right"
                title={VOL_HEADER_TOOLTIP_POLY}
              >{POLY_TOTAL_VOLUME_LABEL}</Th>
              <Th className="w-24 text-right" title="Market row: total $ resting in book (bids + asks). Expanded ladder rows: Resting $ — price × size resting at that price.">P Liq</Th>
              <Th
                className="w-16 text-right"
                title="Polymarket best-bid depth change over the last 1m on the active side. Pair with K Δ/m for cross-exchange sweep imbalance."
              >P Δ/m</Th>
              </>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, eventIndex) => (
              <EventRows
                key={row.key}
                event={row}
                eventIndex={eventIndex}
                marketSide={marketSide}
                priceMode={priceMode}
                positionByTicker={positionByTicker}
                ordersByTicker={ordersByTicker}
                openOrders={openOrders}
                spreadIdx={Math.min(spreadIdxByEvent.get(row.key) ?? 0, Math.max(row.spreads.length - 1, 0))}
                totalIdx={Math.min(totalIdxByEvent.get(row.key) ?? 0, Math.max(row.totals.length - 1, 0))}
                expandedRow={expandedRow?.eventKey === row.key ? expandedRow.rowKind : null}
                onSelectSpreadIdx={(idx) => onSelectSpreadIdx(row.key, idx)}
                onSelectTotalIdx={(idx) => onSelectTotalIdx(row.key, idx)}
                onToggleExpand={(rowKind) => onToggleExpand(row.key, rowKind)}
                onPillTrade={(rowKind, venue, side, cents, meta) =>
                  onPillTrade?.(row.key, rowKind, venue, side, cents, meta)
                }
                showOpColumn={showOpColumn}
                lt2CardScope={LT2_DASH_CARD_SCOPE}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── EventRows — 4 main rows + optional ladder for the expanded row ────

export interface EventRowsProps {
  event: GameEventRow;
  eventIndex: number;
  marketSide: MarketSide;
  priceMode: PriceMode;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  openOrders: KalshiOrder[];
  spreadIdx: number;
  totalIdx: number;
  expandedRow: RowKind | null;
  onSelectSpreadIdx: (idx: number) => void;
  onSelectTotalIdx: (idx: number) => void;
  onToggleExpand: (row: RowKind) => void;
  onInspectSide?: (row: RowKind, side: MarketSide) => void;
  onPillTrade: (
    row: RowKind,
    venue: 'kalshi' | 'polymarket',
    side: 'yes' | 'no',
    cents: number,
    meta?: PillTradeMeta,
  ) => void;
  /** Skip the leading MatchupRow (Date + "Away @ Home"). Consumers like
   *  GameDetail show the matchup + date in the page header already and
   *  the row is redundant noise. ActionRows + ladders still render. */
  hideMatchupRow?: boolean;
  /** Render the OP/PNCL per-line column (fail-closed: default false — the
   *  parent table decides via hasOpLinesFor so the layout is pixel-identical
   *  when the OP source serves no line ladders). */
  showOpColumn?: boolean;
  /** D-020 V2B — hotkey-focus registration scope. When supplied (and the
   *  trader has lt2Access + the hotkey setting on), each derivative row
   *  registers as a focusable card `${keyPrefix}:${event.key}:{spread|total}`
   *  whose resolver reports the row's CURRENT displayed A/D targets.
   *  Omit on surfaces (or periods) outside the hotkey coverage boundary. */
  lt2CardScope?: { surface: Lt2Surface; period: Lt2Period; keyPrefix: string };
  /** #882 — depth and geometry of the SINGLE expanded two-sided ladder
   *  (`TwoSidedLadder`). Omitted = the shared default (three levels,
   *  half-width columns). Deliberately not applied to the nearest-three
   *  Butterfly panel or the non-LT2 single-side ladder: the packet leaves
   *  Butterfly depth open (#884) and the V1 ladder is out of scope. */
  ladderPresentation?: DerivativeLadderPresentation;
  /** #884 — the scope of play these rows belong to ('full', '1H', '2H',
   *  '1Q'…, the Game Lines period ids), for the comparison cell and the
   *  Butterfly cards alike. Omitted = full game, which is every board and
   *  Game Detail Spread/Total consumer today. Independent of LT2 access:
   *  a first-half row must read the first-half reference for every trader. */
  opPeriod?: string;
}

/** One line's OP display payload, pre-aligned to the ROW's yes/no
 *  semantics (spread: YES = favored side covers; total: YES = Over). */
interface OpLineDisplay {
  roi?: { fair: OpFairView; yesIsAway: boolean; strike: number };
  yesTenths: number | null;
  noTenths: number | null;
  mode: 'consensus' | 'anchor-only';
  anchorBookmaker: string | null;
  contributingBooks: number | null;
  pinnacleAnchored: boolean | null;
  /** Integer line — can push; fair values are two-way de-vig as quoted. */
  canPush: boolean;
}

function opLineDisplayFrom(
  view: OpFairView | null,
  yesIsAway: boolean,
  rowLine: number | null | undefined,
): OpLineDisplay | null {
  if (view == null || rowLine == null) return null;
  const yes = yesIsAway ? view.away : view.home;
  const no = yesIsAway ? view.home : view.away;
  const yesTenths = yes.consensusTenths ?? yes.anchorFairTenths ?? null;
  const noTenths = no.consensusTenths ?? no.anchorFairTenths ?? null;
  if (yesTenths == null && noTenths == null) return null;
  return {
    roi: { fair: view, yesIsAway, strike: rowLine },
    yesTenths,
    noTenths,
    mode: view.mode,
    anchorBookmaker: view.anchorBookmaker,
    contributingBooks: view.contributingBooks,
    pinnacleAnchored: view.pinnacleAnchored,
    canPush: opLineCanPush(rowLine),
  };
}

/** One OP/PNCL cell's two value slots, in the order they are DISPLAYED.
 *  Each slot names the market whose tenths it carries, so the pairing is by
 *  identity — reading it never depends on which lane the value landed in. */
export interface OpDisplaySlots {
  leftLabel: string;
  leftTenths: number | null;
  rightLabel: string;
  rightTenths: number | null;
}

/** #651 — the OP/PNCL cell's display order.
 *
 *  The venue pill pairs beside this cell swap lanes with the LT2-018
 *  perspective toggle (`flipPills` → `flex-row-reverse`, both venues). The
 *  comparison cell was left out of that swap when the toggle shipped
 *  (`b9ccb955`, 2026-08-18) even though the OP column had been beside it since
 *  `4419afd3` (2026-08-11) — so with the perspective flipped, OP/PNCL read
 *  favoured-then-underdog while the pills read underdog-then-favoured, and a
 *  left-to-right comparison silently compared opposite sides.
 *
 *  Totals never flip, so Over always holds the leading lane there; this is
 *  what keeps that true for spreads under the toggle as well. */
export function opDisplaySlots(
  op: OpLineDisplay,
  yesLabel: string,
  noLabel: string,
  flipped: boolean,
): OpDisplaySlots {
  return flipped
    ? { leftLabel: noLabel, leftTenths: op.noTenths, rightLabel: yesLabel, rightTenths: op.yesTenths }
    : { leftLabel: yesLabel, leftTenths: op.yesTenths, rightLabel: noLabel, rightTenths: op.noTenths };
}

function opLineTitle(op: OpLineDisplay, slots: OpDisplaySlots): string {
  const parts: string[] = [];
  parts.push(
    op.mode === 'consensus'
      ? `OddsPapi cross-book fair value at this line (${op.contributingBooks ?? '?'} books${op.pinnacleAnchored ? ', pinnacle-anchored' : ''})`
      : `OddsPapi ${op.anchorBookmaker ?? 'anchor'} single-book fair value at this line (no cross-book consensus)`,
  );
  // Named sides, in the same order the cell renders them — a tooltip that
  // contradicted the displayed order would reintroduce the very mismatch.
  parts.push(
    `${slots.leftLabel} ${fairPctText(slots.leftTenths) ?? '—'} / ${slots.rightLabel} ${fairPctText(slots.rightTenths) ?? '—'}`,
  );
  if (op.canPush) parts.push(OP_INTEGER_LINE_NOTE);
  return parts.join('\n');
}

export function EventRows({
  event,
  eventIndex,
  marketSide,
  priceMode,
  positionByTicker,
  ordersByTicker,
  openOrders,
  spreadIdx,
  totalIdx,
  expandedRow,
  onSelectSpreadIdx,
  onSelectTotalIdx,
  onToggleExpand,
  onInspectSide,
  onPillTrade,
  hideMatchupRow = false,
  showOpColumn = false,
  lt2CardScope,
  ladderPresentation,
  opPeriod = 'full',
}: EventRowsProps) {
  const spread: SpreadLine | undefined = event.spreads[spreadIdx];
  const total: TotalLine | undefined = event.totals[totalIdx];
  const isEventExpanded = expandedRow !== null;
  // D-020: the expanded slot swaps to the two-sided ladder under lt2.
  const lt2Access = useLt2Access();
  const lt2HotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);
  const nearestLinesOn = useDisplaySettings((s) => s.spreadTotalNearestLines);
  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const hideUnknownFeePrices = useDisplaySettings((s) => s.hideUnknownFeePrices);
  const hotkeysOn = lt2Access && lt2HotkeysEnabled && lt2CardScope != null;
  const spreadCardId = lt2CardScope ? lt2CardId(lt2CardScope.keyPrefix, event.key, 'spread') : null;
  const totalCardId = lt2CardScope ? lt2CardId(lt2CardScope.keyPrefix, event.key, 'total') : null;
  const spreadFeeTicker = spread?.kalshi?.marketTicker ?? null;
  const totalFeeTicker = total?.kalshi?.marketTicker ?? null;
  const spreadFeeEntry = useFeeFor(spreadFeeTicker);
  const totalFeeEntry = useFeeFor(totalFeeTicker);
  const spreadFeeStatus = useFeeDisplayStatus(spreadFeeTicker, spreadFeeEntry);
  const totalFeeStatus = useFeeDisplayStatus(totalFeeTicker, totalFeeEntry);
  const spreadPriceHidden =
    feeAdjusted && hideUnknownFeePrices && feeDisplayCanHide(spreadFeeStatus);
  const totalPriceHidden =
    feeAdjusted && hideUnknownFeePrices && feeDisplayCanHide(totalFeeStatus);

  // OP fair value at THIS row's selected line — the row's own line IS the
  // main-line selection (no independent heuristic, no wire mainLine flag):
  // whatever line the chip strip has selected, the OP cell shows that
  // line's cross-book de-vig. #884: the reference is resolved by the ONE
  // admitted path the Butterfly cards use (`resolveLineReferenceView`), in
  // the table's own scope of play (`opPeriod`) — a first-half row reads the
  // first-half bucket at its strike, never the full-game value beside it.
  // Full game still goes through the exact full-game getters, so every
  // full-game consumer is unchanged.
  const getOpSpreadLineFor = useMarketStore((s) => s.getOpSpreadLineFor);
  const getOpTotalLineFor = useMarketStore((s) => s.getOpTotalLineFor);
  const getOpMarketsFor = useMarketStore((s) => s.getOpMarketsFor);
  const opVersion = useMarketStore((s) => s.version);
  const { opSpreadDisplay, opTotalDisplay } = useMemo(() => {
    void opVersion;
    if (!showOpColumn) return { opSpreadDisplay: null, opTotalDisplay: null };
    const readers = { spread: getOpSpreadLineFor, total: getOpTotalLineFor, markets: getOpMarketsFor };
    const spreadView = spread ? resolveLineReferenceView(event, 'spread', spread, readers, opPeriod) : null;
    // Totals never swap — Over rides the away slot by sideIndex convention.
    const totalView = total ? resolveLineReferenceView(event, 'total', total, readers, opPeriod) : null;
    return {
      // Spread row YES = the favored side covers.
      opSpreadDisplay: spread
        ? opLineDisplayFrom(spreadView, spread.favored === 'away', spread.line)
        : null,
      // Total row YES = Over (away slot).
      opTotalDisplay: total ? opLineDisplayFrom(totalView, true, total.line) : null,
    };
  }, [showOpColumn, opVersion, getOpSpreadLineFor, getOpTotalLineFor, getOpMarketsFor, event, spread, total, opPeriod]);

  // Maker-fee rebate fraction (0..1) for this event's maker-perspective
  // ladder odds. 0 unless the org is MM-enrolled and the toggle is on; live
  // events rebate 100% of the maker fee, pregame 50%.
  const makerRebateActive = useMakerRebateActive();
  const makerRebateFraction = makerRebateActive
    ? makerRebatePct({ isLive: event.gameState === 'live' })
    : 0;

  // Per-event zebra: every row of one event shares the same background;
  // flip per event.
  const baseRowStyle =
    !isEventExpanded && eventIndex % 2 === 1
      ? { backgroundColor: 'hsl(var(--muted) / var(--zebra-opacity))' }
      : undefined;

  // D-020 LT2-018 — team-side toggle: flip which team's perspective the
  // spread strip reads from. PRESENTATION ONLY: chip labels negate, the
  // description names the other team, and the pill/ladder DISPLAY order
  // swaps so the flipped team's cover side sits left — but every click and
  // hotkey still resolves the SAME underlying ticker and true yes/no side.
  // Market identity, chip ordering (ascending by the displayed sign), and
  // card focus (ids never change) are preserved.
  const [spreadFlipped, setSpreadFlipped] = useState(false);
  const spreadChips = event.spreads.map((sp) => ({
    key: spreadLineKey(sp),
    label: formatSignedLine(spreadFlipped ? -sp.line : sp.line),
    priceCents: linePriceCents(sp),
  }));
  const totalChips = event.totals.map((t) => ({
    key: totalLineKey(t),
    label: t.line.toString(),
    priceCents: linePriceCents(t),
  }));

  /**
   * #758 — an activity row's label, taken from the CHIP it navigates to.
   *
   * Reading it off the chip is what makes the list and the strip agree by
   * construction. Deriving it independently is how the row already had two
   * spellings of one line: the chips render `-3.5` (ASCII hyphen, via
   * `formatSignedLine`) while the order-line text rendered `−3.5` (U+2212, via
   * `formatOrderLine`) — and, worse, the ⇄ perspective flip negates the chip
   * labels and did not negate that text, so with the flip on the row named a
   * line the strip did not have.
   *
   * The fallback only runs for a market the board does not list, which by
   * definition has no chip. It follows the same flip so the two cannot diverge
   * there either.
   */
  const spreadActivityLabel = (l: StripActivityLine): string => {
    if (l.idx != null) return spreadChips[l.idx]?.label ?? '—';
    if (l.line == null) return '—';
    return formatSignedLine(spreadFlipped ? -l.line : l.line);
  };
  const totalActivityLabel = (l: StripActivityLine): string => {
    if (l.idx != null) return totalChips[l.idx]?.label ?? '—';
    return l.line == null ? '—' : l.line.toString();
  };

  // Aggregate positions + open orders across EVERY line in each strip so the
  // C/O rings under the Spread/Total labels show holdings regardless of which
  // chip is currently selected. Walk `openOrders` directly (canonical
  // accountStore data) and parse each ticker — bets on strikes hydra hasn't
  // surfaced still count toward the ring. (The expanded ladder still wires
  // selected-line orders for per-bid-row queue chips.)
  const stripAggregates = useMemo(() => {
    const spreadTickerSet = new Set(
      event.spreads.flatMap((sp) => (sp.kalshi ? [sp.kalshi.marketTicker] : [])),
    );
    const totalTickerSet = new Set(
      event.totals.flatMap((t) => (t.kalshi ? [t.kalshi.marketTicker] : [])),
    );
    const spreadLineByTicker = new Map<string, number>();
    for (const sp of event.spreads) {
      if (sp.kalshi) spreadLineByTicker.set(sp.kalshi.marketTicker, sp.line);
    }
    const totalLineByTicker = new Map<string, number>();
    for (const t of event.totals) {
      if (t.kalshi) totalLineByTicker.set(t.kalshi.marketTicker, t.line);
    }

    // #758 — per-ticker facts, accumulated by the SAME walk that feeds the
    // rings. A second walk would be a second answer, and the failure mode is
    // specific: the ring counts activity the list beside it does not list.
    const spreadFacts = new Map<string, StripActivityFact>();
    const totalFacts = new Map<string, StripActivityFact>();
    const factFor = (facts: Map<string, StripActivityFact>, ticker: string, line: number | null) => {
      const existing = facts.get(ticker);
      if (existing) {
        // A later sighting may resolve a line an earlier one could not; it must
        // never overwrite a resolved line with null.
        if (existing.line == null && line != null) existing.line = line;
        return existing;
      }
      const fresh: StripActivityFact = { line, position: null, ordersYes: 0, ordersNo: 0 };
      facts.set(ticker, fresh);
      return fresh;
    };

    const spreadAgg = { ordersYes: 0, ordersNo: 0, lines: [] as number[], tickers: [] as string[] };
    const totalAgg = { ordersYes: 0, ordersNo: 0, lines: [] as number[], tickers: [] as string[] };

    const sortedEventTeams = [event.awayCode, event.homeCode].sort();

    for (const o of openOrders) {
      if (!o.ticker || o.remainingCount <= 0) continue;
      const ticker = o.ticker;

      // Fast path: order is on a ticker hydra surfaced in this event.
      let line: number | null = null;
      let kind: 'spread' | 'total' | null = null;

      if (spreadTickerSet.has(ticker)) {
        kind = 'spread';
        line = spreadLineByTicker.get(ticker) ?? null;
      } else if (totalTickerSet.has(ticker)) {
        kind = 'total';
        line = totalLineByTicker.get(ticker) ?? null;
      } else {
        // Slow path: parse ticker (venue-agnostic) and match to this
        // event by sport+date+teams. The kalshi spread/total category
        // check below still gates the kalshi-specific outcome math; poly
        // tickers fall through here as untyped for this slow-path slot
        // until the polymarket spread/total row model lands.
        const parsed = parseTicker(ticker);
        if (!parsed.game || !parsed.date) continue;
        if (parsed.date !== event.date) continue;
        const sp = [parsed.game.awayCode, parsed.game.homeCode].sort();
        if (sp[0] !== sortedEventTeams[0] || sp[1] !== sortedEventTeams[1]) continue;

        if (parsed.category === `${event.sport}-spread`) {
          const m = parsed.outcome?.match(/^([A-Z]+)(\d+)$/);
          if (!m) continue;
          const teamCode = m[1];
          const bucket = Number(m[2]);
          const lineMag = parseLineFromOrderTicker(ticker, 'spread') ?? bucket - 0.5;
          const favored: 'away' | 'home' = teamCode === event.awayCode ? 'away' : 'home';
          kind = 'spread';
          line = favored === 'away' ? -lineMag : lineMag;
        } else if (parsed.category === `${event.sport}-total`) {
          const m = parsed.outcome?.match(/^(\d+)$/);
          if (!m) continue;
          const strike = Number(m[1]);
          const minMargin = event.sport === 'mlb' || event.sport === 'wnba';
          kind = 'total';
          line =
            parseLineFromOrderTicker(ticker, 'total') ??
            (minMargin ? strike - 0.5 : strike + 0.5);
        }
      }

      if (!kind) continue;
      const agg = kind === 'spread' ? spreadAgg : totalAgg;
      const fact = factFor(kind === 'spread' ? spreadFacts : totalFacts, ticker, line);
      if (o.side === 'yes') agg.ordersYes += 1;
      else agg.ordersNo += 1;
      if (o.side === 'yes') fact.ordersYes += 1;
      else fact.ordersNo += 1;
      if (line !== null && !agg.lines.includes(line)) agg.lines.push(line);
      if (!agg.tickers.includes(ticker)) agg.tickers.push(ticker);
    }

    spreadAgg.lines.sort((a, b) => a - b);
    totalAgg.lines.sort((a, b) => a - b);

    // Positions aggregate by visible-line ticker only — a held contract
    // implies hydra has surfaced its market frame.
    let spreadPosYes = 0, spreadPosNo = 0, totalPosYes = 0, totalPosNo = 0;
    // Per-line detail for the ring's hover (#161). The counts above span every
    // line in the strip, so one average across them would blend unrelated
    // strikes; the hover lists each held line separately instead. Same shape
    // as `lines` above, which enumerates the lines carrying open orders.
    const spreadPosLines: StripPosLine[] = [];
    const totalPosLines: StripPosLine[] = [];
    for (const tk of spreadTickerSet) {
      const p = positionByTicker.get(tk);
      if (!p || p.yesCount + p.noCount <= 0) continue;
      spreadPosYes += p.yesCount;
      spreadPosNo += p.noCount;
      const spLine = spreadLineByTicker.get(tk) ?? null;
      spreadPosLines.push({ line: spLine, pos: p });
      factFor(spreadFacts, tk, spLine).position = p;
    }
    for (const tk of totalTickerSet) {
      const p = positionByTicker.get(tk);
      if (!p || p.yesCount + p.noCount <= 0) continue;
      totalPosYes += p.yesCount;
      totalPosNo += p.noCount;
      const tLine = totalLineByTicker.get(tk) ?? null;
      totalPosLines.push({ line: tLine, pos: p });
      factFor(totalFacts, tk, tLine).position = p;
    }
    // Ascending by line, matching the inline position labels and order-ring
    // hover. A line we could not resolve sorts last rather than being dropped
    // because the holding is still real.
    const byLine = (a: StripPosLine, b: StripPosLine) =>
      (a.line ?? Number.POSITIVE_INFINITY) - (b.line ?? Number.POSITIVE_INFINITY);
    spreadPosLines.sort(byLine);
    totalPosLines.sort(byLine);

    // Reachability comes from the SAME arrays the chips are built from, so an
    // entry's index and its chip can never point at different markets.
    const idxOf = (lines: Array<{ kalshi?: { marketTicker: string } }>) => {
      const m = new Map<string, number>();
      lines.forEach((l, i) => {
        if (l.kalshi) m.set(l.kalshi.marketTicker, i);
      });
      return m;
    };

    // Built once per aggregation pass, not per render: this row is one of six
    // on a board #730 is already trying to stop re-computing, and a fresh Set
    // on every render would defeat any memo a caller later adds.
    const spreadActivity = buildStripActivityLines(spreadFacts, idxOf(event.spreads));
    const totalActivity = buildStripActivityLines(totalFacts, idxOf(event.totals));

    return {
      spread: {
        ...spreadAgg,
        posYes: spreadPosYes,
        posNo: spreadPosNo,
        posLines: spreadPosLines,
        workingOrderActivity: workingOrderActivityLines(spreadActivity),
        workingOrderKeys: workingOrderTickerSet(spreadActivity),
      },
      total: {
        ...totalAgg,
        posYes: totalPosYes,
        posNo: totalPosNo,
        posLines: totalPosLines,
        workingOrderActivity: workingOrderActivityLines(totalActivity),
        workingOrderKeys: workingOrderTickerSet(totalActivity),
      },
    };
  }, [event, openOrders, positionByTicker]);
  const spreadStripAgg = stripAggregates.spread;
  const totalStripAgg = stripAggregates.total;

  const unit = sportUnit(event.sport);
  const spreadDogCode = spread
    ? spread.favored === 'away'
      ? event.homeCode
      : event.awayCode
    : '';
  const spreadDescription = spread
    ? spreadFlipped
      ? // Dog perspective — the NO side of the same market, phrased as the
        // cover the flipped team's backer is buying. Never a new market.
        `${spreadDogCode} +${Math.abs(spread.line)} covers (NO side)`
      : buildSpreadDescription({
          favoredName: spread.favored === 'away' ? event.awayCode : event.homeCode,
          mag: Math.abs(spread.line),
          unit,
        })
    : null;
  const totalDescription = total ? buildTotalDescription({ line: total.line, unit }) : null;

  const isSpreadExpanded = expandedRow === 'spread';
  const isTotalExpanded = expandedRow === 'total';

  // ── D-020 V2B hotkey-focus registration ─────────────────────────────
  // Resolvers report the row's CURRENT displayed A/D targets: collapsed →
  // the Y/N (O/U) pill pair, A = left pill; expanded two-sided ladder →
  // each column's best displayed BID (the top-of-book maker action),
  // A = left column. Same price derivations the mouse surfaces use.
  const lt2TargetsFor = (
    kind: 'spread' | 'total',
    line: SpreadLine | TotalLine | undefined,
    expanded: boolean,
  ): Lt2CardTargets | null => {
    if (!lt2CardScope || !line) return null;
    const book = line.kalshi ?? null;
    const availability = classifyLineAvailability(book);
    const lineValue =
      kind === 'spread' ? (line as SpreadLine).line : (line as TotalLine).line;
    const nameFor = (side: 'yes' | 'no'): string => {
      if (kind === 'total') return `${side === 'yes' ? 'Over' : 'Under'} ${lineValue}`;
      const sp = line as SpreadLine;
      const favCode = sp.favored === 'away' ? event.awayCode : event.homeCode;
      const dogCode = sp.favored === 'away' ? event.homeCode : event.awayCode;
      const mag = Math.abs(sp.line);
      return side === 'yes' ? `${favCode} −${mag}` : `${dogCode} +${mag}`;
    };
    const targetFor = (side: 'yes' | 'no'): Lt2HotkeyTarget => {
      // A hidden price is unavailable for BOTH pointer and keyboard input.
      // Otherwise the fail-closed fee display would merely hide a still-live
      // execution target from the trader.
      const priceHidden = kind === 'spread' ? spreadPriceHidden : totalPriceHidden;
      const priceCents = priceHidden
        ? null
        : expanded
          ? (buildSideLevels(book, side).bids[0]?.priceCents ?? null)
          : pillPriceCents(book, side, priceMode);
      const postOnly = expanded ? true : priceMode === 'maker';
      return {
        book,
        side,
        priceCents,
        postOnly,
        label: `${nameFor(side)} ${side.toUpperCase()}${priceCents != null ? ` @ ${priceCents}¢` : ''}`,
        availability,
        // W (left/top slot) / E (right/bottom slot) attack one level above
        // this side's displayed best bid, in the side's own cents —
        // independent of the collapsed pill's price mode and of expansion,
        // because the reference is the passive book level itself.
        attack: deriveAttackTarget(book, side, priceHidden),
      };
    };
    // The team-side toggle swaps DISPLAY order; A always targets the LEFT
    // displayed side, so a flipped spread row maps A to the NO (dog) side.
    const flipped = kind === 'spread' && spreadFlipped;
    // Expanded spread/total rows still paint a live price row (the ladder's
    // join bid), so they are never 'paused' — only the moneyline suppresses.
    const cardLabel =
      kind === 'total'
        ? `O/U ${lineValue}`
        : (() => {
            const sp = line as SpreadLine;
            const favCode = sp.favored === 'away' ? event.awayCode : event.homeCode;
            return `${favCode} −${Math.abs(sp.line)}`;
          })();
    return {
      a: targetFor(flipped ? 'no' : 'yes'),
      d: targetFor(flipped ? 'yes' : 'no'),
      surface: lt2CardScope.surface,
      marketKind: kind,
      period: lt2CardScope.period,
      line: lineValue,
      cardLabel,
      // The same synthesized GameRow the pill-click path trades through —
      // its key IS the event's canonical game key, which is all the exact-
      // game cancel selection consumes as the target.
      pullScope: {
        game:
          kind === 'spread'
            ? synthesizeFromSpread(event, line as SpreadLine)
            : synthesizeFromTotal(event, line as TotalLine),
      },
      pausedReason: null,
    };
  };
  const butterflyMembership = (kind: 'spread' | 'total', selected: number, expanded: boolean) => {
    if (!lt2Access || !nearestLinesOn || !expanded) return null;
    const lines = kind === 'spread' ? event.spreads : event.totals;
    const keyFor = (line: SpreadLine | TotalLine) => JSON.stringify([line.line,
      kind === 'spread' ? (line as SpreadLine).favored : null, line.kalshi?.marketTicker ?? null]);
    return { selected: lines[selected] ? keyFor(lines[selected]) : null,
      members: JSON.stringify(nearestLineWindow(lines.length, selected).map(i => keyFor(lines[i]))) };
  };
  const membership = {
    spread: butterflyMembership('spread', spreadIdx, isSpreadExpanded),
    total: butterflyMembership('total', totalIdx, isTotalExpanded),
  };
  const previousMembership = useRef(membership);
  useLayoutEffect(() => {
    for (const kind of ['spread', 'total'] as const) {
      const before = previousMembership.current[kind];
      const after = membership[kind];
      const cardId = kind === 'spread' ? spreadCardId : totalCardId;
      if (cardId && before && after && before.selected === after.selected && before.members !== after.members) {
        useLt2FocusStore.getState().clearFocusIf(cardId);
      }
    }
    previousMembership.current = membership;
  });
  const spreadFocused = useLt2Card(spreadCardId, hotkeysOn, () =>
    lt2TargetsFor('spread', spread, isSpreadExpanded),
  );
  const totalFocused = useLt2Card(totalCardId, hotkeysOn, () =>
    lt2TargetsFor('total', total, isTotalExpanded),
  );

  return (
    <Fragment>
      {/* Matchup label — "Away @ Home" on one row; favored team brightens
       *  as the user scrolls the spread chips across zero. Tints dark when
       *  the spread is open since matchup + spread row pair on the bet.
       *  Skipped when the parent surface (GameDetail) already shows the
       *  matchup in its page header. */}
      {!hideMatchupRow && (
        <MatchupRow
          eventIndex={eventIndex}
          date={event.date}
          timePt={event.timePt}
          startTimeMs={event.startTimeMs}
          gameState={event.gameState}
          sport={event.sport}
          gameKey={event.key}
          awayCode={event.awayCode}
          homeCode={event.homeCode}
          awayName={event.awayName}
          homeName={event.homeName}
          favored={spread?.favored ?? null}
          rowStyle={baseRowStyle}
          active={isSpreadExpanded}
          showOpColumn={showOpColumn}
        />
      )}

      {/* Spread action row — chip strip + Y/N pills + market data. */}
      <ActionRow
        eventIndex={eventIndex}
        chips={spreadChips}
        selectedChipIdx={spreadIdx}
        onSelectChipIdx={onSelectSpreadIdx}
        stripLabel="Spread"
        description={spreadDescription}
        kalshiBook={spread?.kalshi ?? null}
        polyBook={spread?.polymarket ?? null}
        marketSide={marketSide}
        priceMode={priceMode}
        stripPosYes={spreadStripAgg.posYes}
        stripPosNo={spreadStripAgg.posNo}
        stripPosLines={spreadStripAgg.posLines}
        stripOrdersYes={spreadStripAgg.ordersYes}
        stripOrdersNo={spreadStripAgg.ordersNo}
        stripOrderLines={spreadStripAgg.lines}
        stripOrderLineKind="spread"
        stripWorkingOrderActivity={spreadStripAgg.workingOrderActivity}
        stripActivityLabel={spreadActivityLabel}
        onSelectActivityTicker={(ticker) => {
          // Resolved HERE, against the array this row is rendering, so the
          // index never outlives the arrangement it describes. A ticker the
          // board no longer lists selects nothing rather than whatever has
          // since moved into its slot.
          const i = event.spreads.findIndex((l) => l.kalshi?.marketTicker === ticker);
          if (i >= 0) onSelectSpreadIdx(i);
        }}
        stripWorkingOrderKeys={spreadStripAgg.workingOrderKeys}
        isExpanded={isSpreadExpanded}
        rowStyle={baseRowStyle}
        onToggle={() => onToggleExpand('spread')}
        onInspectSide={onInspectSide ? side => onInspectSide('spread', side) : undefined}
        onPillTrade={(venue, side, cents, meta) => onPillTrade('spread', venue, side, cents, meta)}
        yesLabel="Y"
        noLabel="N"
        showOpColumn={showOpColumn}
        op={opSpreadDisplay}
        lt2FocusId={hotkeysOn ? spreadCardId : null}
        lt2Focused={spreadFocused}
        flipPills={spreadFlipped}
        onFlipPerspective={lt2Access ? () => setSpreadFlipped((f: boolean) => !f) : undefined}
        perspectiveFlipped={spreadFlipped}
      />

      {/* Spread ladder slot — directly below the spread action row. Under
          lt2 the slot renders BOTH sides adjacently (LT2-003) — or the
          nearest-three window when the multi-line setting is on (LT2-017);
          without lt2 the V1 single-side ladder is untouched. */}
      {isSpreadExpanded && spread && lt2Access && nearestLinesOn && (
        <NearestLinesPanel
          event={event}
          period={opPeriod}
          colSpan={6 + (showOpColumn ? 1 : 0)}
          kind="spread"
          lines={event.spreads}
          selectedIdx={spreadIdx}
          yesLabel="YES"
          noLabel="NO"
          makerRebatePct={makerRebateFraction}
          userOrdersFor={(ticker) => mergeOrdersForTicker(ticker, openOrders, ordersByTicker)}
          onLevelClick={(lineIdx, side, kind, cents) =>
            onPillTrade('spread', 'kalshi', side, cents, { origin: 'ladder', kind, lineIdx })
          }
          focused={spreadFocused}
          lt2FocusId={hotkeysOn ? spreadCardId : null}
          flipColumns={spreadFlipped}
        />
      )}
      {isSpreadExpanded && spread && lt2Access && !nearestLinesOn && (
        <TwoSidedLadder
          kalshi={spread.kalshi ?? null}
          yesLabel="YES"
          noLabel="NO"
          userOrders={
            spread.kalshi
              ? mergeOrdersForTicker(spread.kalshi.marketTicker, openOrders, ordersByTicker)
              : null
          }
          availability={classifyLineAvailability(spread.kalshi)}
          makerRebatePct={makerRebateFraction}
          onLevelClick={(side, kind, cents) =>
            onPillTrade('spread', 'kalshi', side, cents, { origin: 'ladder', kind })
          }
          focused={spreadFocused}
          lt2FocusId={hotkeysOn ? spreadCardId : null}
          flipColumns={spreadFlipped}
          presentation={ladderPresentation}
          reference={onInspectSide && showOpColumn ? opSpreadDisplay?.roi : undefined}
        />
      )}
      {isSpreadExpanded && spread && !lt2Access && (
        <LadderRowsFor
          venueBookKalshi={spread.kalshi ?? null}
          venueBookPoly={spread.polymarket ?? null}
          flipped={false}
          showOpColumn={showOpColumn}
          marketSide={marketSide}
          makerRebatePct={makerRebateFraction}
          userOrders={
            spread.kalshi
              ? mergeOrdersForTicker(spread.kalshi.marketTicker, openOrders, ordersByTicker)
              : null
          }
          onLevelClick={(venue, cents, kind, side) =>
            onPillTrade('spread', venue, side, cents, { origin: 'ladder', kind })
          }
        />
      )}

      {/* Total action row — chip strip selects the line; O/U pills are
       *  Over/Under prices on that line (O = Over = YES, U = Under = NO).
       *  No dedicated label row; the chip strip itself communicates the
       *  line value. O/U labels carry through to the K Vol / P Vol cell's
       *  stacked depth split (so traders read "O 281K / U 240K" directly
       *  instead of decoding Y/N → Over/Under). */}
      <ActionRow
        eventIndex={eventIndex}
        chips={totalChips}
        selectedChipIdx={totalIdx}
        onSelectChipIdx={onSelectTotalIdx}
        stripLabel="Total"
        description={totalDescription}
        kalshiBook={total?.kalshi ?? null}
        polyBook={total?.polymarket ?? null}
        marketSide={marketSide}
        priceMode={priceMode}
        stripPosYes={totalStripAgg.posYes}
        stripPosNo={totalStripAgg.posNo}
        stripPosLines={totalStripAgg.posLines}
        stripOrdersYes={totalStripAgg.ordersYes}
        stripOrdersNo={totalStripAgg.ordersNo}
        stripOrderLines={totalStripAgg.lines}
        stripOrderLineKind="total"
        stripWorkingOrderActivity={totalStripAgg.workingOrderActivity}
        stripActivityLabel={totalActivityLabel}
        onSelectActivityTicker={(ticker) => {
          const i = event.totals.findIndex((l) => l.kalshi?.marketTicker === ticker);
          if (i >= 0) onSelectTotalIdx(i);
        }}
        stripWorkingOrderKeys={totalStripAgg.workingOrderKeys}
        isExpanded={isTotalExpanded}
        rowStyle={baseRowStyle}
        onToggle={() => onToggleExpand('total')}
        onInspectSide={onInspectSide ? side => onInspectSide('total', side) : undefined}
        onPillTrade={(venue, side, cents, meta) => onPillTrade('total', venue, side, cents, meta)}
        yesLabel="O"
        noLabel="U"
        showOpColumn={showOpColumn}
        op={opTotalDisplay}
        lt2FocusId={hotkeysOn ? totalCardId : null}
        lt2Focused={totalFocused}
      />

      {/* Total ladder slot — directly below the total action row. Two-sided
          (or nearest-three) under lt2, V1 single-side otherwise. */}
      {isTotalExpanded && total && lt2Access && nearestLinesOn && (
        <NearestLinesPanel
          event={event}
          period={opPeriod}
          colSpan={6 + (showOpColumn ? 1 : 0)}
          kind="total"
          lines={event.totals}
          selectedIdx={totalIdx}
          yesLabel="OVER"
          noLabel="UNDER"
          makerRebatePct={makerRebateFraction}
          userOrdersFor={(ticker) => mergeOrdersForTicker(ticker, openOrders, ordersByTicker)}
          onLevelClick={(lineIdx, side, kind, cents) =>
            onPillTrade('total', 'kalshi', side, cents, { origin: 'ladder', kind, lineIdx })
          }
          focused={totalFocused}
          lt2FocusId={hotkeysOn ? totalCardId : null}
        />
      )}
      {isTotalExpanded && total && lt2Access && !nearestLinesOn && (
        <TwoSidedLadder
          kalshi={total.kalshi ?? null}
          yesLabel="OVER"
          noLabel="UNDER"
          userOrders={
            total.kalshi
              ? mergeOrdersForTicker(total.kalshi.marketTicker, openOrders, ordersByTicker)
              : null
          }
          availability={classifyLineAvailability(total.kalshi)}
          makerRebatePct={makerRebateFraction}
          onLevelClick={(side, kind, cents) =>
            onPillTrade('total', 'kalshi', side, cents, { origin: 'ladder', kind })
          }
          focused={totalFocused}
          lt2FocusId={hotkeysOn ? totalCardId : null}
          presentation={ladderPresentation}
          reference={onInspectSide && showOpColumn ? opTotalDisplay?.roi : undefined}
        />
      )}
      {isTotalExpanded && total && !lt2Access && (
        <LadderRowsFor
          venueBookKalshi={total.kalshi ?? null}
          venueBookPoly={total.polymarket ?? null}
          flipped={false}
          showOpColumn={showOpColumn}
          marketSide={marketSide}
          makerRebatePct={makerRebateFraction}
          userOrders={
            total.kalshi
              ? mergeOrdersForTicker(total.kalshi.marketTicker, openOrders, ordersByTicker)
              : null
          }
          onLevelClick={(venue, cents, kind, side) =>
            onPillTrade('total', venue, side, cents, { origin: 'ladder', kind })
          }
        />
      )}
    </Fragment>
  );
}

// ── MatchupRow — single "Away @ Home" header row per event ─────────────

interface MatchupRowProps {
  eventIndex: number;
  date: string;
  /** Local-time event start ("7:00p") or empty when unknown. Renders below
   *  the date in the same cell. */
  timePt: string;
  /** Epoch ms event start — drives the pre-game pulse dot + countdown text. */
  startTimeMs: number | null;
  /** Drives the LIVE pulse dot, FINAL chip, and final-row dim. Same
   *  three-state model the moneyline GameTable uses. */
  gameState: 'live' | 'pre' | 'final';
  /** Sport — needed so formatGameStatus can compose the right period
   *  label (Bot 3rd vs Q2 5:32 vs 2nd Set). */
  sport: Sport;
  /** Canonical game key — resolved to the hydra/Unabated game key for
   *  the gameState lookup. */
  gameKey: string;
  /** Display-oriented team codes for the game-detail deep link. */
  awayCode: string;
  homeCode: string;
  awayName: string;
  homeName: string;
  /** Which team is on the YES side of the current spread ticker, if any.
   *  null when there is no spread for this event (totals-only). */
  favored: 'away' | 'home' | null;
  rowStyle: React.CSSProperties | undefined;
  /** True when this event's spread is open — pairs the matchup with the
   *  spread action row visually via the dark tint. */
  active: boolean;
  /** The matchup band's colSpan grows by one when the OP column renders. */
  showOpColumn?: boolean;
}

function MatchupRow({
  eventIndex,
  date,
  timePt,
  startTimeMs,
  gameState,
  sport,
  gameKey,
  awayCode,
  homeCode,
  awayName,
  homeName,
  favored,
  rowStyle,
  active,
  showOpColumn = false,
}: MatchupRowProps) {
  const borderClass = eventIndex > 0 ? 'border-t-2 border-border' : '';
  const butterflyAccess = useLt2Access();
  const butterflyOn = useDisplaySettings((s) => s.spreadTotalNearestLines) && butterflyAccess;
  // Pull Unabated game state for live period text + scores. The poll
  // upstream in SpreadsTotals/index.tsx keeps the store fresh; this
  // hook just reads. Resolves to null pre-game (statusId 1) and when
  // the key isn't in the store yet.
  const hydraGameKey = toHydraGameKeyLive(gameKey);
  const liveGameState = useGameStateFor(hydraGameKey);
  // Status chip state (#121) — canonical statusId, not the clock-based
  // `gameState` heuristic, which calls a postponed game live for as long as
  // its book stays open. Same helper the moneyline GameTable uses, so both
  // views resolve identically.
  //
  // Canonical state is INJECTED and this view has no producer yet: the
  // `gameStatesStore` read above is fed by /api/analytics/game-states, which
  // is not a live service, so the chip must not depend on it — and must not
  // fall back to a clock-derived LIVE either. A started game therefore renders
  // UNKNOWN until Hydra #19 lands a bounded source, at which point this one
  // call gains its argument. The store read stays for the pre-existing
  // live-scores display, which is outside this slice.
  const chipState = resolveLiveDisplayState(null, gameState);
  const isLive = chipState === 'live';
  // Row treatment (the dimmed opacity below) stays on the row heuristic, the
  // same split GameTable uses: chips read `chipState`, row treatment does not.
  // Identical today — with nothing injected, `chipState === 'final'` reduces
  // to exactly this — but keeping them separate means a future canonical final
  // can't silently start dimming rows as a side effect of a chip change.
  const isFinalRow = gameState === 'final';
  const isFinal = chipState === 'final';
  // Show scores any time the game is live or final per Unabated.
  // Falls back to the event.gameState gate when Unabated hasn't
  // populated yet so the row still reads consistently.
  const showScores =
    liveGameState != null &&
    (liveGameState.statusId === 2 || liveGameState.statusId === 3);
  const awayScore = showScores ? liveGameState.awayScore : null;
  const homeScore = showScores ? liveGameState.homeScore : null;
  // Live period label ("Bot 3rd", "Q2 5:32", etc.) replaces the
  // scheduled date+time in the left column once the game starts.
  // Only renders for live (statusId 2) — finals get their FINAL chip,
  // pre-game keeps the date+countdown.
  const liveStatusText =
    liveGameState && liveGameState.statusId === 2
      ? formatGameStatus(liveGameState, sport)
      : null;
  const awayClass =
    favored == null
      ? 'text-foreground'
      : favored === 'away'
        ? 'text-foreground'
        : 'text-muted-foreground/70';
  const homeClass =
    favored == null
      ? 'text-foreground'
      : favored === 'home'
        ? 'text-foreground'
        : 'text-muted-foreground/70';
  return (
    <tr
      className={cn(
        borderClass,
        active && 'bg-black/15 dark:bg-black/95',
        isFinalRow && 'opacity-60',
      )}
      style={rowStyle}
    >
      <Td className="text-muted-foreground font-mono text-sm">
        <Link
          to={gameDetailHrefForRow({ key: gameKey, sport, awayCode, homeCode })
            ?? `/game/${date}/${awayCode}/${homeCode}`}
         
          className="hover:text-foreground hover:underline focus:outline-none focus-visible:underline flex flex-col leading-tight"
          title="Open game detail"
        >
          <span className="flex items-center gap-1">
            {chipState === 'pre' && startTimeMs != null && (
              <NearEventStartPulse startTimeMs={startTimeMs} />
            )}
            {isFinal ? (
              <span
                className="inline-block rounded border border-zinc-500/60 bg-zinc-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400"
                title="Game finalized"
              >
                FINAL
              </span>
            ) : isLive ? (
              // Was a bare 6px green dot. That is the exact cue LiveBadge was
              // built to replace — "a trader opened pregame-style orders on an
              // already-live game because the only cue was a 6px dot" — and
              // #121 asks for ONE consistent treatment, so this view now shows
              // the same chip the moneyline board does.
              <LiveBadge />
            ) : chipState === 'unknown' ? (
              <UnknownStatusBadge />
            ) : (
              date
            )}
          </span>
          {!isFinal && (
            isLive ? (
              // Live → the Unabated period status ("Bot 3rd"), or an explicit
              // dash when no status feed exists — never the stale start time
              // beside a live cue (Joshua, Jun 6).
              <span className="text-foreground font-mono text-[11px]">
                {liveStatusText ?? '—'}
              </span>
            ) : chipState === 'unknown' ? (
              // Nothing at all. The clock has passed the start with nothing to
              // confirm it, so the scheduled time would read as current — but a
              // bare dash under an UNKNOWN chip is doubly empty and reads as a
              // rendering glitch. The chip already carries the whole message.
              // (A POSTPONED game resolves to 'pre' instead and keeps its time
              // below, matching the Date cell.)
              null
            ) : timePt ? (
              <span className="flex items-center gap-1.5">
                <span className="text-foreground font-mono text-[11px]">
                  {timePt}
                </span>
                {startTimeMs != null && <GameCountdown startTimeMs={startTimeMs} />}
              </span>
            ) : null
          )}
        </Link>
      </Td>
      {/* One band across every remaining column. The name pair used to sit in
       *  the `w-72` Side & Line column with a short trailing filler beside it,
       *  which cost twice over once #805 put real names here: "East Carolina"
       *  wrapped mid-name against a column sized for a chip strip, and the
       *  under-spanned filler left the band stopping short of the table's
       *  right edge. Spanning the row is what a header band is; it also means
       *  a long pair widens nothing below it. */}
      <Td colSpan={(showOpColumn ? 10 : 9) - (butterflyOn ? 4 : 0)} className="font-medium whitespace-nowrap">
        <span className={awayClass}>{awayName}</span>
        {showScores && awayScore != null && (
          <span className="text-foreground ml-1.5 font-mono tabular-nums">
            ({awayScore})
          </span>
        )}
        <span className="text-muted-foreground/40 mx-2">@</span>
        <span className={homeClass}>{homeName}</span>
        {showScores && homeScore != null && (
          <span className="text-foreground ml-1.5 font-mono tabular-nums">
            ({homeScore})
          </span>
        )}
      </Td>
    </tr>
  );
}

// ── ActionRow — chip strip + Y/N pills + market data on one row ────────

interface ActionRowProps {
  eventIndex: number;
  chips: Array<{ key: string; label: string; priceCents: number | null }>;
  selectedChipIdx: number;
  onSelectChipIdx: (idx: number) => void;
  stripLabel: string;
  /** Plain-English bet description rendered above the chip strip — e.g.
   *  "St. Louis wins by over 3.5 runs" or "Over 11.5 runs scored". The
   *  selected line value is inlined and highlighted by the helpers. */
  description: React.ReactNode | null;
  kalshiBook: VenueBook | null;
  polyBook: VenueBook | null;
  marketSide: MarketSide;
  priceMode: PriceMode;
  /** Strip-wide aggregates across ALL lines in this strip (not just the
   *  selected chip). Drive the 'C' / 'O' rings under the Spread/Total
   *  label so holdings on any line stay visible regardless of which
   *  chip is currently selected. Counts come from accountStore.openOrders
   *  (stable across hydra ticks); per-bid-row queue chips still source
   *  from ordersByTicker for the selected line only. */
  stripPosYes: number;
  stripPosNo: number;
  /** Per-line breakdown behind stripPosYes/stripPosNo — the 'C' hover names
   *  each held line with its own average rather than blending across them. */
  stripPosLines: StripPosLine[];
  stripOrdersYes: number;
  stripOrdersNo: number;
  /** Distinct line values the user has open orders on within this strip,
   *  sorted ascending. Feeds the 'O' ring's hover text. */
  stripOrderLines: number[];
  stripOrderLineKind: 'spread' | 'total';
  /** #773 — every position-bearing line, shown inline and ascending. */
  stripWorkingOrderActivity: readonly StripActivityLine[];
  /** The chip label for a position line; sourced from the corresponding chip. */
  stripActivityLabel: (line: StripActivityLine) => string;
  /** Select the strip line carrying this ticker. Resolved to an index by the
   *  row, against the line array it is rendering this very pass. */
  onSelectActivityTicker: (ticker: string) => void;
  /**
   * #777 — tickers carrying a WORKING ORDER. Held contracts remain visible
   * through the separate C ring and do not light this order-only mark.
   */
  stripWorkingOrderKeys: ReadonlySet<string>;
  isExpanded: boolean;
  rowStyle: React.CSSProperties | undefined;
  onToggle: () => void;
  onInspectSide?: (side: MarketSide) => void;
  /** Fired when an individual pill is clicked. Carries enough context for
   *  the parent to seed the GameDetailPanel order ticket (or do nothing). */
  onPillTrade: (
    venue: 'kalshi' | 'polymarket',
    side: 'yes' | 'no',
    cents: number,
    meta?: PillTradeMeta,
  ) => void;
  yesLabel: string;     // 'Y' for spread, 'O' for total
  noLabel: string;      // 'N' for spread, 'U' for total
  /** OP/PNCL per-line column (hydracore §"Spread & total markets"). */
  showOpColumn?: boolean;
  op?: OpLineDisplay | null;
  /** D-020 V2B — this row's hotkey-focus card id (null = focus disabled).
   *  A row click FOCUSES via capture (without eating the expand toggle);
   *  `lt2Focused` paints the persistent indicator. */
  lt2FocusId?: string | null;
  lt2Focused?: boolean;
  /** D-020 LT2-018 — swap the two venue pill pairs' DISPLAY order (the
   *  flipped team's cover side renders left). Labels and each pill's true
   *  side are untouched; only the order flips. */
  flipPills?: boolean;
  /** Renders the ⇄ perspective toggle beside the description (spread rows
   *  under lt2 only). */
  onFlipPerspective?: () => void;
  perspectiveFlipped?: boolean;
}

function ActionRow({
  chips,
  selectedChipIdx,
  onSelectChipIdx,
  stripLabel,
  description,
  kalshiBook,
  polyBook,
  marketSide,
  priceMode,
  stripPosYes,
  stripPosNo,
  stripPosLines,
  stripOrdersYes,
  stripOrdersNo,
  stripOrderLines,
  stripOrderLineKind,
  stripWorkingOrderActivity,
  stripActivityLabel,
  onSelectActivityTicker,
  stripWorkingOrderKeys,
  isExpanded,
  rowStyle,
  onToggle,
  onInspectSide,
  onPillTrade,
  yesLabel,
  noLabel,
  showOpColumn = false,
  op = null,
  lt2FocusId = null,
  lt2Focused = false,
  flipPills = false,
  onFlipPerspective,
  perspectiveFlipped = false,
}: ActionRowProps) {
  // Match moneyline GameTable: pill price honours the Maker/Taker/Market
  // toolbar toggle via `pillPriceCents`, with NO derived from its own
  // orderbook side (NO ask = 100 − best YES bid) instead of `100 − YES_mid`.
  const butterflyAccess = useLt2Access();
  const butterflyOn = useDisplaySettings((s) => s.spreadTotalNearestLines) && butterflyAccess;
  const yesCents = pillPriceCents(kalshiBook, 'yes', priceMode);
  const noCents = pillPriceCents(kalshiBook, 'no', priceMode);
  // D-020 LT2-004: five-state availability drives the Kalshi pill disables
  // (was `null-book || stale` — suspended and mapping-error rows could still
  // fire) and the explanatory row badge. Absent = the Poly-only line case.
  // GATED on lt2Access (review finding, 2026-08-18): with the flag off the
  // pills keep V1's exact disable rule and no badge renders — byte-identical
  // flag-off behavior is the release invariant. The submit-time guard still
  // protects every lt2 order regardless of what the pill allowed.
  const rowLt2Access = useLt2Access();
  const kalshiAvailability = classifyLineAvailability(kalshiBook);
  const kalshiPillDisabled = rowLt2Access
    ? kalshiAvailability !== 'active'
    : kalshiBook == null || kalshiBook.freshness === 'stale';
  const showAvailabilityBadge = rowLt2Access && kalshiAvailability !== 'active';
  const polyYesCents = pillPriceCents(polyBook, 'yes', priceMode);
  const polyNoCents = pillPriceCents(polyBook, 'no', priceMode);

  // #167 — crossed-book tripwire, one read per venue book. Book-level and
  // side-symmetric, so a single read qualifies both of that venue's pills.
  // Derived per render, so it clears itself when the book heals or ownership
  // falls back to a sane source.
  const kalshiCrossed = crossedBookRead(kalshiBook, 'kalshi');
  const polyCrossed = crossedBookRead(polyBook, 'polymarket');

  // Per-user activity rings (matches GameTable's PosCell). 'C' splits the
  // ring by yes/no contract count; 'O' aggregates open-order count by
  // side. Both aggregate across the entire strip — a user's holdings on
  // the over-9.5 line still surface when the over-2.5 chip is selected.
  const posYes = stripPosYes;
  const posNo = stripPosNo;
  const ordersYes = stripOrdersYes;
  const ordersNo = stripOrdersNo;
  const orderTooltipText =
    stripOrderLines.length > 0
      ? `Open orders on ${stripOrderLines.map((l) => formatOrderLine(l, stripOrderLineKind)).join(', ')}`
      : '';

  // #609 — the hotkey fence reads these two marks off the DOM. The focus mark
  // says "part of the focused card"; the price mark says "this row shows the
  // price A/D would submit". Collapsed, that is this row's pills. Expanded,
  // `lt2TargetsFor` reads the ladder's join bid instead, so the price mark
  // moves to the ladder row below and the fence checks THAT row's visibility —
  // otherwise a visible action row would certify a price under the fold.
  const lt2FocusMark = lt2Focused ? (lt2FocusId ?? undefined) : undefined;
  const lt2PriceMark = lt2Focused && !isExpanded ? '' : undefined;

  return (
    <tr
      data-view-anchor={JSON.stringify([stripLabel, kalshiBook?.marketTicker ?? null, polyBook?.marketTicker ?? null])}
      className={cn(
        // Hover matches the selected-row tint so it's clear the row is a
        // click target. `!` important is load-bearing — alternate rows
        // set background-color via inline `style` (rowStyle), and inline
        // styles outrank plain Tailwind utilities including hover. Same
        // treatment as GameTable's moneyline rows.
        'hover:!bg-black/15 dark:hover:!bg-black/95 cursor-pointer transition-colors',
        isExpanded && 'bg-black/15 dark:bg-black/95',
        // Persistent hotkey-focus treatment — !important so the zebra
        // rowStyle's inline background can't hide it.
        lt2Focused && '!bg-primary/15',
      )}
      style={rowStyle}
      // #648 — focus moves on the BUBBLE, behind the shared focus-region
      // predicate. It used to move in the capture phase, which React runs
      // before any descendant's handler, so a price pill's stopPropagation
      // could suppress the expansion but never stop the pill from retargeting
      // the hotkeys. Expansion runs first and unconditionally: it is the
      // established card-selection behavior and #648 does not touch it.
      onClick={(e) => {
        onToggle();
        if (lt2FocusId && isLt2FocusRegionClick(e.target, e.currentTarget)) {
          useLt2FocusStore.getState().toggleFocus(lt2FocusId);
        }
      }}
      data-lt2-focused={lt2FocusMark}
      data-lt2-price={lt2PriceMark}
      data-lt2-hotkey-control={lt2Focused || undefined}
    >
      <Td className="text-muted-foreground/70 py-1 text-[10px] uppercase tracking-wider">
        <div className="flex flex-col items-start gap-1">
          <span className="flex items-center gap-1">
            {stripLabel}
            {lt2Focused && lt2FocusId && (
              <Lt2FocusBadge
                sides="A posts the left/top side, D the right/bottom"
                onClear={() => useLt2FocusStore.getState().clearFocusIf(lt2FocusId)}
              />
            )}
          </span>
          {(posYes + posNo > 0 || ordersYes + ordersNo > 0) && (
            <div className="flex max-w-full flex-wrap items-center gap-x-1 gap-y-0.5">
              {posYes + posNo > 0 && (
                <span
                  className="inline-flex items-center"
                  title={heldContractsByLineTitle(
                    stripPosLines.map((e) => ({
                      lineLabel: e.line != null ? formatOrderLine(e.line, stripOrderLineKind) : null,
                      pos: e.pos,
                    })),
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <PosRing yesCount={posYes} noCount={posNo} variant="closed" size={16} />
                </span>
              )}
              <StripWorkingOrderLinks
                lines={stripWorkingOrderActivity}
                labelFor={stripActivityLabel}
                kindLabel={stripLabel}
                onSelect={onSelectActivityTicker}
                onFocusCard={
                  lt2FocusId
                    ? () => useLt2FocusStore.getState().focusIfFree(lt2FocusId)
                    : undefined
                }
              />
              {ordersYes + ordersNo > 0 && (
                <span
                  className="inline-flex items-center"
                  title={orderTooltipText}
                  onClick={(e) => e.stopPropagation()}
                >
                  <PosRing yesCount={ordersYes} noCount={ordersNo} variant="open" size={16} />
                </span>
              )}
            </div>
          )}
        </div>
      </Td>
      <Td className="py-1">
        <div className="flex flex-col items-start gap-1">
          {description && (
            <div className="flex items-center gap-1.5">
              <div className="text-foreground text-xs font-medium">{description}</div>
              {onFlipPerspective && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFlipPerspective();
                  }}
                  title={
                    perspectiveFlipped
                      ? 'Showing the underdog perspective (+lines, NO side left). Click for the favorite perspective.'
                      : 'Flip to the other team’s perspective (+lines, their cover side left). Same markets — display only.'
                  }
                  className={cn(
                    'rounded border px-1 py-px text-[10px] font-semibold transition-colors',
                    perspectiveFlipped
                      ? 'border-primary/50 bg-primary/15 text-primary'
                      : 'border-border/60 text-muted-foreground hover:text-foreground',
                  )}
                >
                  ⇄
                </button>
              )}
              {showAvailabilityBadge && (
                <span
                  className="rounded border border-amber-500/40 bg-amber-500/10 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-500"
                  title={
                    kalshiAvailability === 'absent'
                      ? 'No usable Kalshi market for this line — order entry disabled'
                      : `Kalshi market ${LINE_AVAILABILITY_LABEL[kalshiAvailability].toLowerCase()} — order entry disabled`
                  }
                >
                  {LINE_AVAILABILITY_LABEL[kalshiAvailability]}
                </span>
              )}
            </div>
          )}
          {chips.length > 0 ? (
            <MiniLineStrip
              chips={chips}
              selectedIdx={selectedChipIdx}
              onSelect={onSelectChipIdx}
              markedKeys={stripWorkingOrderKeys}
              onFocusCard={
                lt2FocusId
                  ? () => useLt2FocusStore.getState().focusIfFree(lt2FocusId)
                  : undefined
              }
            />
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          )}
        </div>
      </Td>

      {/* OP/PNCL — OddsPapi fair value AT the selected line, rendered in the
          SAME lane order as the venue pills beside it: `opDisplaySlots`
          follows the LT2-018 perspective flip so a left-to-right read always
          compares the same side (#651). '—' = no OP coverage at this line
          (rule 9), never an error — and note that is the live state whenever
          the OP source serves moneyline only. The teal echoes the panel's 'O'
          row (OP vocabulary — never gold, that's Unabated's). */}
      {showOpColumn && (
        <Td className="whitespace-nowrap text-center">
          {/* Reserve the fair-value line plus both ROI lines even while the
              exact reference is absent. Feed arrival must not move prices. */}
          <div className="flex h-12 min-w-[16rem] flex-col" data-action-reference="">
          {op != null ? (
            (() => {
              const slots = opDisplaySlots(op, yesLabel, noLabel, flipPills);
              return (
                <>
                <span
                  className={cn(
                    'block h-4 leading-4 font-mono text-[11px] tabular-nums text-teal-600 dark:text-teal-300',
                    op.mode === 'anchor-only' && 'underline decoration-dotted underline-offset-2',
                  )}
                  title={opLineTitle(op, slots)}
                  data-op-order={`${slots.leftLabel}/${slots.rightLabel}`}
                >
                  {fairPctText(slots.leftTenths) ?? '—'}
                  <span className="text-muted-foreground/70"> / </span>
                  {fairPctText(slots.rightTenths) ?? '—'}
                  {op.canPush && <span className="text-muted-foreground/70" title={OP_INTEGER_LINE_NOTE}> ⁼</span>}
                </span>
                {op.roi && <LineTheoreticalRoi {...op.roi} book={kalshiBook} flipped={flipPills} />}
                </>
              );
            })()
          ) : (
            <span className="h-4 text-muted-foreground text-xs leading-4">—</span>
          )}
          </div>
        </Td>
      )}

      <Td className="whitespace-nowrap text-center">
        <div
          className={cn(
            'flex items-center justify-center gap-1.5',
            // LT2-018 — display order only; each pill keeps its true side.
            flipPills && 'flex-row-reverse',
          )}
        >
          <PricePill
            label={yesLabel}
            interaction={onInspectSide ? 'view' : 'trade'}
            cents={yesCents}
            side="yes"
            sideTint
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={kalshiPillDisabled}
            ticker={kalshiBook?.marketTicker ?? null}
            crossedBook={kalshiCrossed}
            onClick={(e) => {
              e.stopPropagation();
              if (yesCents == null) return;
              if (onInspectSide) { onInspectSide('yes'); return; }
              onPillTrade('kalshi', 'yes', yesCents, { origin: 'pill' });
            }}
          />
          <PricePill
            label={noLabel}
            interaction={onInspectSide ? 'view' : 'trade'}
            cents={noCents}
            side="no"
            sideTint
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={kalshiPillDisabled}
            ticker={kalshiBook?.marketTicker ?? null}
            crossedBook={kalshiCrossed}
            onClick={(e) => {
              e.stopPropagation();
              if (noCents == null) return;
              if (onInspectSide) { onInspectSide('no'); return; }
              onPillTrade('kalshi', 'no', noCents, { origin: 'pill' });
            }}
          />
        </div>
      </Td>

      <VolumeCell book={kalshiBook} yesLabel={yesLabel} noLabel={noLabel} />
      {/* Fixed-ch spans on live numeric cells: digit-count changes must not
          alter column content width or the auto table layout re-flows on
          every tick (page jitter). Same pattern as GameTable's GameRows. */}
      <Td className="text-muted-foreground text-right font-mono text-xs">
        <span className="inline-block w-[7ch] text-right">
          {kalshiBook ? formatCompactCurrency(kalshiBook.liquidity) : '—'}
        </span>
      </Td>

      {/* K Δ/m — 1m best-bid depth velocity on the active side (#665:
          warming/ready/unavailable via the shared lifecycle). */}
      {(() => {
        const kDisplay = depthVelocityDisplay(
          kalshiBook?.marketTicker ? depthKeyForBook(kalshiBook, marketSide) : null,
        );
        return (
          <Td
            className={cn(
              'text-right font-mono text-xs',
              depthDeltaTone(kDisplay.kind === 'ready' ? kDisplay.delta : null),
            )}
          >
            <span className="inline-block w-[7ch] text-right">
              <DepthVelocityValue display={kDisplay} />
            </span>
          </Td>
        );
      })()}

      {!butterflyOn && <>
      <Td className="whitespace-nowrap text-center">
        <div
          className={cn(
            'flex items-center justify-center gap-1.5',
            flipPills && 'flex-row-reverse',
          )}
        >
          <PricePill
            label={yesLabel}
            interaction={onInspectSide ? 'view' : 'trade'}
            cents={polyYesCents}
            side="yes"
            sideTint
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={polyBook == null || polyBook.freshness === 'stale'}
            ticker={polyBook?.marketTicker ?? null}
            crossedBook={polyCrossed}
            onClick={(e) => {
              e.stopPropagation();
              if (polyYesCents == null) return;
              if (onInspectSide) { onInspectSide('yes'); return; }
              onPillTrade('polymarket', 'yes', polyYesCents, { origin: 'pill' });
            }}
          />
          <PricePill
            label={noLabel}
            interaction={onInspectSide ? 'view' : 'trade'}
            cents={polyNoCents}
            side="no"
            sideTint
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={polyBook == null || polyBook.freshness === 'stale'}
            ticker={polyBook?.marketTicker ?? null}
            crossedBook={polyCrossed}
            onClick={(e) => {
              e.stopPropagation();
              if (polyNoCents == null) return;
              if (onInspectSide) { onInspectSide('no'); return; }
              onPillTrade('polymarket', 'no', polyNoCents, { origin: 'pill' });
            }}
          />
        </div>
      </Td>

      <VolumeCell book={polyBook} yesLabel={yesLabel} noLabel={noLabel} />
      {/* P Liq — the SAME component the moneyline table renders, so the
          shared-book marker, its tooltip and the cell metrics can't drift
          between the two surfaces. */}
      <Td className="text-muted-foreground text-right font-mono text-xs">
        <AggregateLiquidityCell book={polyBook} />
      </Td>

      {/* P Δ/m — Polymarket sibling of K Δ/m (#665 lifecycle). */}
      {(() => {
        const pDisplay = depthVelocityDisplay(
          polyBook?.marketTicker ? depthKeyForBook(polyBook, marketSide) : null,
        );
        return (
          <Td
            className={cn(
              'text-right font-mono text-xs',
              depthDeltaTone(pDisplay.kind === 'ready' ? pDisplay.delta : null),
            )}
          >
            <span className="inline-block w-[7ch] text-right">
              <DepthVelocityValue display={pDisplay} />
            </span>
          </Td>
        );
      })()}
      </>}
    </tr>
  );
}

// ── VolumeCell ────────────────────────────────────────────────────────
// K Vol / P Vol for spread/total lines: cumulative TRADED volume on this
// line's market — the same semantic basis as the moneyline table's K Vol
// (Dan, 2026-07-07: the stacked per-side numbers previously shown here were
// RESTING book depth, which read as sold volume under a "Vol" header).
//
// A true per-side bought-up split does not exist on the feed: a total line
// is one binary market (Over/Under are its YES/NO sides) and Kalshi/hydra
// send a single aggregate traded volume per market. The per-side resting
// depth (VenueBook.{yes,no}Volume — the old primary display) moves to the
// tooltip, explicitly labeled as resting, until hydra can derive taker-side
// traded volume from the trade stream.
export function VolumeCell({
  book,
  yesLabel,
  noLabel,
}: {
  book: VenueBook | null;
  yesLabel: string;
  noLabel: string;
}) {
  if (!book) {
    return (
      <Td className="text-muted-foreground text-right font-mono text-xs">—</Td>
    );
  }
  const hasDepth = book.yesVolume != null && book.noVolume != null;
  // #154 (D2 / C1): when the aggregate is the depth-derived fallback (hydra
  // reported no cumulative traded volume), the number under the "Vol" header is
  // RESTING DEPTH, not executed volume — so never label it "Traded". Mirror the
  // moneyline AggregateVolumeCell cue: a leading "~" + italic + "resting depth"
  // wording. No numeric change.
  const depthDerived = book.volumeIsDepthDerived === true && book.volume > 0;
  // #154 (D3): a poly spread/total is ONE binary book (Over/Under are its two
  // sides), so this aggregate is shared — mark it with the same shared-book marker
  // the moneyline uses so it doesn't read as an independent per-outcome total.
  const shared = book.isSharedBinaryBook === true;
  const perSideDepth = hasDepth
    ? ` Resting book depth (not traded): ${yesLabel} ${formatVolume(book.yesVolume!)} / ${noLabel} ${formatVolume(book.noVolume!)}`
    : '';
  const sharedNote = shared
    ? ' Shared market-wide total — one binary book underlies both outcomes, not an independent per-outcome total.'
    : '';
  const title =
    (depthDerived
      ? `Resting depth (bids + asks): ${formatVolume(book.volume)} contracts — no cumulative traded volume reported for this line; NOT executed volume.${perSideDepth}`
      : `Traded: ${formatVolume(book.volume)} contracts.${perSideDepth}`) + sharedNote;
  return (
    <Td className="text-muted-foreground text-right font-mono text-xs" title={title}>
      <span className={cn('inline-block w-[6ch] text-right', depthDerived && 'italic')}>
        {depthDerived && <span aria-hidden>~</span>}
        {formatVolume(book.volume)}
        {shared && (
          <span className="text-muted-foreground/70 align-super text-[0.6em]" aria-hidden>
            s
          </span>
        )}
      </span>
    </Td>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────


/** Sport-appropriate unit for the bet-description sentence. */
function sportUnit(sport: Sport): string {
  switch (sport) {
    case 'mlb':
      return 'runs';
    case 'nhl':
    case 'soccer':
      return 'goals';
    case 'atp':
    case 'wta':
      return 'games';
    case 'ufc':
      return 'rounds';
    case 'nba':
    case 'wnba':
    case 'cbb':
    default:
      return 'points';
  }
}

function buildSpreadDescription({
  favoredName,
  mag,
  unit,
}: {
  favoredName: string;
  mag: number;
  unit: string;
}): React.ReactNode {
  return (
    <span>
      {favoredName} wins by over <span className="text-positive">{mag}</span> {unit}
    </span>
  );
}

function buildTotalDescription({
  line,
  unit,
}: {
  line: number;
  unit: string;
}): React.ReactNode {
  return (
    <span>
      Over <span className="text-positive">{line}</span> {unit} scored
    </span>
  );
}

// ── LadderRowsFor — ask/last/bid block below an expanded perspective row ─

interface LadderRowsForProps {
  venueBookKalshi: VenueBook | null;
  venueBookPoly: VenueBook | null;
  flipped: boolean;
  /** OP/PNCL column present in the host table — caption + ladder rows
   *  emit one extra spacer cell to stay aligned. */
  showOpColumn?: boolean;
  marketSide: MarketSide;
  /** Maker-fee rebate fraction (0..1) folded into maker-perspective odds;
   *  0 = none. Forwarded to each LadderLineRow. */
  makerRebatePct: number;
  /** User's open orders on the Kalshi ticker — chips render on the matching
   *  bid/ask row keyed by priceCents. Filtered to the currently-selected
   *  marketSide (same convention as moneyline's GameTable). */
  userOrders: UserOrderSlice[] | null;
  /** Fires when the user clicks a Kalshi or Poly bid/ask price in the
   *  inline ladder. Carries the click price plus the LEVEL's bid/ask kind
   *  and the ladder's effective book side — a bid click is a join (maker /
   *  post-only), an ask click may cross; the old two-arg shape dropped that
   *  distinction and forced the toolbar marketSide (D-020 postOnly-fidelity
   *  fix). The parent decides how to open the trade modal / one-click. */
  onLevelClick: (
    venue: 'kalshi' | 'polymarket',
    cents: number,
    kind: 'bid' | 'ask',
    side: 'yes' | 'no',
  ) => void;
}

function LadderRowsFor({
  venueBookKalshi,
  venueBookPoly,
  flipped,
  showOpColumn = false,
  marketSide,
  makerRebatePct,
  userOrders,
  onLevelClick,
}: LadderRowsForProps) {
  // For a flipped row, the toolbar marketSide picks within the row's
  // perspective, which is opposite to the book's. Resolve to the effective
  // side on the BOOK before building the ladder.
  const effectiveMarketSide: MarketSide = flipped
    ? marketSide === 'yes'
      ? 'no'
      : 'yes'
    : marketSide;

  // Shared level math (buildSideLevels) — one implementation with the LT2
  // TwoSidedLadder so a level's price can never mean two things.
  const k = buildSideLevels(venueBookKalshi, effectiveMarketSide);
  const p = buildSideLevels(venueBookPoly, effectiveMarketSide);

  const kMaxLiq = venueBookKalshi && venueBookKalshi.liquidity > 0 ? venueBookKalshi.liquidity : 1;
  const pMaxLiq = venueBookPoly && venueBookPoly.liquidity > 0 ? venueBookPoly.liquidity : 1;

  const padTo = (rows: BookLevel[]): (BookLevel | null)[] => {
    const out: (BookLevel | null)[] = [...rows];
    while (out.length < LADDER_LEVELS) out.push(null);
    return out;
  };

  // Asks worst-at-top, best-at-bottom near strip; bids best-at-top.
  const kAsksVisual = padTo([...k.asks].reverse());
  const kBidsVisual = padTo(k.bids);
  const pAsksVisual = padTo([...p.asks].reverse());
  const pBidsVisual = padTo(p.bids);

  const lastFor = (book: VenueBook | null): number | null => {
    const ly = book?.lastPriceCents ?? null;
    if (ly == null) return null;
    return effectiveMarketSide === 'yes' ? ly : mirrorCents(ly);
  };

  const kLast = lastFor(venueBookKalshi);
  const pLast = lastFor(venueBookPoly);
  // Per-price-level Kalshi user-order chips — same pattern as moneyline's
  // GameTable. BUY orders ride bid rows; SELL orders ride ask rows. Filter
  // to the side the ladder is currently expanded for so chips don't bleed
  // between yes/no toggles. The qty sort below is a stable input order only:
  // #676 puts the display ranking in `RestingOrderGroup`, where the cut is.
  const buildOrdersByCents = (action: 'buy' | 'sell') => {
    const map = new Map<number, BidRowOrder[]>();
    if (!userOrders || !venueBookKalshi) return map;
    for (const o of userOrders) {
      if (o.action !== action) continue;
      if (o.side !== effectiveMarketSide) continue;
      const depth = queueDepth(o, venueBookKalshi);
      const list = map.get(o.priceCents) ?? [];
      list.push({
        orderId: o.orderId,
        remainingCount: o.remainingCount,
        queueOrBetter: depth.atLevelOrBetter,
        tooltip: orderTooltip(o, venueBookKalshi),
        // #676 — see GameTable's twin: confirmed vs remembered decides rank.
        queueRetained: o.queueRetained,
      });
      map.set(o.priceCents, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => b.remainingCount - a.remainingCount);
    }
    return map;
  };
  const kBuyOrdersByCents = buildOrdersByCents('buy');
  const kSellOrdersByCents = buildOrdersByCents('sell');

  return (
    <>
      {/* #154 (D1): visible truthful caption for the expanded ladder. */}
      <LadderLineColumnCaption showOpColumn={showOpColumn} />
      {kAsksVisual.map((kLvl, idx) => (
        <LadderLineRow
          key={`ask-${idx}`}
          kLevel={kLvl}
          pLevel={pAsksVisual[idx] ?? null}
          kMaxLiq={kMaxLiq}
          pMaxLiq={pMaxLiq}
          kTicker={venueBookKalshi?.marketTicker ?? null}
          pTicker={venueBookPoly?.marketTicker ?? null}
          kLastCents={kLast}
          pLastCents={pLast}
          kind="ask"
          showOpColumn={showOpColumn}
          marketSide={marketSide}
          makerRebatePct={makerRebatePct}
          kOrderSide={effectiveMarketSide}
          kUserOrders={kLvl ? kSellOrdersByCents.get(kLvl.priceCents) : undefined}
          onKalshiClick={(cents) => onLevelClick('kalshi', cents, 'ask', effectiveMarketSide)}
          onPolyClick={(cents) => onLevelClick('polymarket', cents, 'ask', effectiveMarketSide)}
        />
      ))}

      {/* Last-trade strip removed — the matching row now wears an
          animate-last-trade-pulse grey halo. */}

      {/* Asks ↔ bids separator. Same faint hairline the moneyline
          ladder uses; colSpan oversized to safely cover every column
          layout this table renders in. */}
      <tr aria-hidden="true">
        <td colSpan={12} className="border-border/40 border-t p-0" />
      </tr>

      {kBidsVisual.map((kLvl, idx) => (
        <LadderLineRow
          key={`bid-${idx}`}
          kLevel={kLvl}
          pLevel={pBidsVisual[idx] ?? null}
          kMaxLiq={kMaxLiq}
          pMaxLiq={pMaxLiq}
          kTicker={venueBookKalshi?.marketTicker ?? null}
          pTicker={venueBookPoly?.marketTicker ?? null}
          kLastCents={kLast}
          pLastCents={pLast}
          kind="bid"
          showOpColumn={showOpColumn}
          marketSide={marketSide}
          makerRebatePct={makerRebatePct}
          kOrderSide={effectiveMarketSide}
          kUserOrders={kLvl ? kBuyOrdersByCents.get(kLvl.priceCents) : undefined}
          onKalshiClick={(cents) => onLevelClick('kalshi', cents, 'bid', effectiveMarketSide)}
          onPolyClick={(cents) => onLevelClick('polymarket', cents, 'bid', effectiveMarketSide)}
        />
      ))}
    </>
  );
}

// ── LadderLineColumnCaption ──────────────────────────────────────────

/**
 * #154 (D1) — VISIBLE column caption for the expanded spread/total ladder.
 * Mirror of GameTable's LadderColumnCaption for this table's LadderLineRow
 * column layout. The shared "K Vol"/"K Liq" headers describe the MARKET line
 * (traded volume / total book $); the ladder's per-price cells are resting
 * contract qty and resting $, so the expanded ladder carries its own truthful
 * "Resting Qty" / "Resting $" caption. Presentation only.
 */
function LadderLineColumnCaption({ showOpColumn = false }: { showOpColumn?: boolean }) {
  const qty = (
    <span className="text-muted-foreground/70 inline-block w-full whitespace-nowrap text-right text-[9px] font-semibold uppercase tracking-wide">
      Resting Qty
    </span>
  );
  const dollars = (
    <span className="text-muted-foreground/70 whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide">
      Resting $
    </span>
  );
  return (
    <tr
      className="border-0"
      title="Expanded ladder — resting book (contracts / $ resting at each price), NOT traded volume"
    >
      {/* Side & Line spacer + user-order column */}
      <Td className="py-0.5">{' '}</Td>
      <Td className="py-0.5">{' '}</Td>
      {/* OP/PNCL spacer */}
      {showOpColumn && <Td className="py-0.5">{' '}</Td>}
      {/* Kalshi price spacer */}
      <Td className="py-0.5">{' '}</Td>
      {/* K Size → Resting Qty */}
      <Td className="py-0.5 text-right">{qty}</Td>
      {/* K Liquidity → Resting $ */}
      <Td className="py-0.5">{dollars}</Td>
      {/* K Δ/m spacer */}
      <Td className="py-0.5">{' '}</Td>
      {/* Poly price spacer */}
      <Td className="py-0.5">{' '}</Td>
      {/* P Size → Resting Qty */}
      <Td className="py-0.5 text-right">{qty}</Td>
      {/* P Liquidity → Resting $ */}
      <Td className="py-0.5">{dollars}</Td>
      {/* P Δ/m spacer */}
      <Td className="py-0.5">{' '}</Td>
    </tr>
  );
}

// ── LadderLineRow — one ask or bid row across both venues ─────────────

interface LadderLineRowProps {
  kLevel: BookLevel | null;
  pLevel: BookLevel | null;
  /** Host table renders the OP/PNCL column — emit the alignment spacer. */
  showOpColumn?: boolean;
  kMaxLiq: number;
  pMaxLiq: number;
  kTicker: string | null;
  pTicker: string | null;
  /** Side-native last trade for each venue book. Drives the slow grey
   *  pulse on whichever row's price matches it (replaces the removed
   *  per-venue "Trade {Side} / Last X¢" strip row). */
  kLastCents?: number | null;
  pLastCents?: number | null;
  kind: 'ask' | 'bid';
  /** Active Y/N toggle. Price cells align under the matching pill — left
   *  (under Y) for 'yes', right (under N) for 'no' — so the book hangs
   *  beneath whichever side is selected. */
  marketSide: MarketSide;
  /** Maker-fee rebate fraction (0..1) folded into maker-perspective odds;
   *  0 = none. */
  makerRebatePct: number;
  /** The side of the BOOK these rows are built from. On a flipped row this
   *  is the opposite of `marketSide`, which is the toolbar's perspective —
   *  own orders were bucketed by this one, so #523's level identity must
   *  name it too. */
  kOrderSide: MarketSide;
  /** User open orders matching this Kalshi row's price (qty + queue, qty
   *  desc). Now rendered in the freed-up Side & Line column (was the
   *  Ask/Bid label) as inert chips; clicking the price still opens the
   *  trade modal. */
  kUserOrders?: BidRowOrder[];
  /** Click handler for the Kalshi price cell. Fires with the level's
   *  cents value so the parent can open a trade modal prefilled to that
   *  price. */
  onKalshiClick: (cents: number) => void;
  onPolyClick: (cents: number) => void;
}

function LadderLineRow({
  kLevel,
  pLevel,
  showOpColumn = false,
  kMaxLiq,
  pMaxLiq,
  kTicker,
  pTicker,
  kLastCents,
  pLastCents,
  kind,
  marketSide,
  makerRebatePct,
  kOrderSide,
  kUserOrders,
  onKalshiClick,
  onPolyClick,
}: LadderLineRowProps) {
  const hasUserOrders = kUserOrders && kUserOrders.length > 0;
  const kLiq = kLevel ? Math.round((kLevel.priceCents * kLevel.size) / 100) : 0;
  const pLiq = pLevel ? Math.round((pLevel.priceCents * pLevel.size) / 100) : 0;
  const colorClass = kind === 'ask' ? 'text-negative' : 'text-positive';
  const kIsLastTrade = kLastCents != null && kLevel?.priceCents === kLastCents;
  const pIsLastTrade = pLastCents != null && pLevel?.priceCents === pLastCents;

  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const hideUnknownFeePrices = useDisplaySettings((s) => s.hideUnknownFeePrices);
  const kFeeEntry = useFeeFor(kTicker);
  const pFeeEntry = useFeeFor(pTicker);
  const resolvedKFeeStatus = useFeeDisplayStatus(kTicker, kFeeEntry);
  const kFeeStatus = feeAdjusted ? resolvedKFeeStatus : 'not-applicable';
  const hideKalshiPrice = hideUnknownFeePrices && feeDisplayCanHide(kFeeStatus);
  // The cents are the raw venue level. Adjacent odds follow what the row
  // represents: ask/crossing is taker; bid/resting is maker. This is the same
  // pure presentation contract used by the moneyline ladder.
  const kPrice = kLevel
    ? ladderRowPricePresentation(kLevel.priceCents, kind, feeAdjusted, kFeeEntry, makerRebatePct)
    : null;
  const pPrice = pLevel
    ? ladderRowPricePresentation(pLevel.priceCents, kind, feeAdjusted, pFeeEntry, makerRebatePct)
    : null;
  const kDisplayCents = hideKalshiPrice ? null : kPrice?.rawCents ?? null;
  const kOdds = hideKalshiPrice || !kPrice ? null : americanOdds(kPrice.effectiveCents);
  const pOdds = pPrice ? americanOdds(pPrice.effectiveCents) : null;

  return (
    <tr className="border-0">
      <Td className="py-0.5"> </Td>
      {/* Your active Kalshi orders at this price level (qty / queue-or-better,
          qty desc) — this is the Side & Line column the Ask/Bid label used to
          occupy. Direction is conveyed by the price color and which half of
          the ladder (above/below the spread hairline) the row sits in, so the
          label was redundant. Right-aligned, flush against the price column.
          Chips are direct-cancel controls (RestingOrderChip, parity with the
          moneyline ladder), led by #523's CancelLevelControl which withdraws
          every own order at this exact level in one frozen action; the wrapper
          stops propagation so gap clicks stay inert. #650: at most
          INLINE_CHIP_LIMIT draw inline, the rest behind a `+N` disclosure —
          see RestingOrderGroup. */}
      <Td className="py-0.5 text-right">
        {hasUserOrders ? (
          <div
            className="text-foreground/90 flex items-center justify-end gap-1.5 overflow-hidden text-[10px] leading-4"
            onClick={(e) => e.stopPropagation()}
          >
            <RestingOrderGroup
              orders={kUserOrders!}
              level={
                kTicker && kLevel
                  ? {
                      marketTicker: kTicker,
                      side: kOrderSide,
                      // Ask row = the SELL stack, bid row = the BUY stack, matching
                      // the split buildOrdersByCents bucketed these orders by.
                      action: kind === 'ask' ? 'sell' : 'buy',
                      priceCents: kLevel.priceCents,
                    }
                  : null
              }
            />
          </div>
        ) : (
          ''
        )}
      </Td>
      {/* OP/PNCL alignment spacer */}
      {showOpColumn && <Td className="py-0.5"> </Td>}
      {/* Kalshi price + American odds. Aligns under the active pill — left
          (under Y) for 'yes', right (under N) for 'no'. */}
      <Td
        className={cn(
          'whitespace-nowrap py-0.5 font-mono',
          marketSide === 'yes' ? 'text-left' : 'text-right',
        )}
      >
        {kLevel ? (
          <span
            className={cn(
              'inline-flex cursor-pointer items-center gap-1 px-1 hover:underline',
              hideKalshiPrice && 'cursor-default hover:no-underline',
              kIsLastTrade && 'animate-last-trade-pulse',
            )}
            data-trade-ticker={kTicker}
            data-trade-venue="kalshi"
            onClick={hideKalshiPrice ? undefined : (e) => {
              e.stopPropagation();
              onKalshiClick(kLevel.priceCents);
            }}
            title={
              hideKalshiPrice
                ? FEE_DISPLAY_MESSAGE[kFeeStatus] ?? undefined
                : kPrice && kPrice.effectiveCents !== kPrice.rawCents
                  ? `${kIsLastTrade ? 'Last trade' : 'Click to trade'} @ ${kPrice.rawCents}¢ raw; Fees odds ${kOdds ?? '—'} use ${kPrice.effectiveCents}¢ ${kPrice.perspective} basis`
                  : `${kIsLastTrade ? 'Last trade' : 'Click to trade'} @ ${kPrice?.rawCents ?? kLevel.priceCents}¢${kOdds ? ` → ${kOdds}` : ''}`
            }
          >
            <span className={cn(colorClass)}>
              {kDisplayCents != null ? `${kDisplayCents}¢` : '—'}
            </span>
            {kOdds && (
              <>
                <span className="text-muted-foreground/40 text-xs">|</span>
                <span className="text-foreground text-xs">{kOdds}</span>
              </>
            )}
            <FeeScheduleMarker status={kFeeStatus} />
          </span>
        ) : null}
      </Td>
      <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
        {kLevel ? (
          <span
            className="inline-block w-[6ch] text-right"
            title="Resting Qty — contracts resting at this price level (not traded volume)"
          >{formatVolume(kLevel.size)}</span>
        ) : ''}
      </Td>
      <Td className="py-0.5" title="Resting $ — price × size resting at this price level">
        <LiquidityBar value={kLiq} maxValue={kMaxLiq} height={16} />
      </Td>
      {/* K Δ/m spacer — velocity column lives on the action row. */}
      <Td className="py-0.5">{' '}</Td>
      <Td
        className={cn(
          'whitespace-nowrap py-0.5 font-mono',
          marketSide === 'yes' ? 'text-left' : 'text-right',
        )}
      >
        {pLevel ? (
          <span
            className={cn(
              'inline-flex cursor-pointer items-center gap-1 px-1 hover:underline',
              pIsLastTrade && 'animate-last-trade-pulse',
            )}
            onClick={(e) => {
              e.stopPropagation();
              onPolyClick(pLevel.priceCents);
            }}
            data-trade-ticker={pTicker}
            data-trade-venue="polymarket"
            title={pIsLastTrade ? `Last trade @ ${pLevel.priceCents}¢` : `Click to trade @ ${pLevel.priceCents}¢`}
          >
            <span className={cn(colorClass)}>{pLevel.priceCents}¢</span>
            {pOdds && (
              <>
                <span className="text-muted-foreground/40 text-xs">|</span>
                <span className="text-foreground text-xs">{pOdds}</span>
              </>
            )}
          </span>
        ) : null}
      </Td>
      <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
        {pLevel ? (
          <span
            className="inline-block w-[6ch] text-right"
            title="Resting Qty — contracts resting at this price level (not traded volume)"
          >{formatVolume(pLevel.size)}</span>
        ) : ''}
      </Td>
      <Td className="py-0.5" title="Resting $ — price × size resting at this price level">
        <LiquidityBar value={pLiq} maxValue={pMaxLiq} height={16} />
      </Td>
      {/* P Δ/m spacer — velocity column lives on the action row. */}
      <Td className="py-0.5">{' '}</Td>
    </tr>
  );
}
